// ═══════════════════════════════════════════════════════════════
// CRM INSTASOLUTIONS — CONSTANTES
// ═══════════════════════════════════════════════════════════════

export const CLIENT_STAGES = [
  { id: "novo_lead", label: "Novo Lead", color: "#94a3b8", help: "Lead identificado mas ainda não contatado. Registre aqui assim que encontrar uma empresa com frota." },
  { id: "primeiro_contato", label: "1º Contato", color: "#6366f1", help: "Primeiro contato feito (WhatsApp, telefone ou indicação). Registre o resultado da conversa." },
  { id: "apresentacao", label: "Apresentação", color: "#8b5cf6", help: "Apresentação da plataforma agendada ou realizada. Use o material de apresentação padrão." },
  { id: "qualificacao", label: "Qualificação", color: "#3b82f6", help: "Lead qualificado: tem frota, tem necessidade, tem poder de decisão. Se não qualificou, mova para 'Descartado'." },
  { id: "proposta", label: "Proposta Enviada", color: "#f59e0b", help: "Proposta comercial enviada. Encaminhe para validação da Gerente de Operações ANTES de enviar." },
  { id: "negociacao", label: "Negociação", color: "#0d9488", help: "Cliente está analisando. Faça follow-up a cada 2-3 dias úteis. Registre cada contato." },
  { id: "fechamento", label: "Fechamento", color: "#10b981", help: "Contrato em assinatura. Repasse ao Financeiro (limite de crédito, serviços, prazo) e ao Suporte (ativação)." },
  { id: "ganho", label: "Ganho", color: "#059669", help: "Contrato assinado! Agora o Suporte faz onboarding e o Financeiro configura faturamento." },
  { id: "perdido", label: "Perdido", color: "#ef4444", help: "Registre o MOTIVO da perda. Isso ajuda a melhorar nossas abordagens futuras." },
  { id: "descartado", label: "Descartado", color: "#cbd5e1", help: "Lead não qualificado: sem frota suficiente, sem interesse, ou não é perfil." },
];

export const PARTNER_STAGES = [
  { id: "identificado", label: "Identificado", color: "#94a3b8", help: "Estabelecimento encontrado na região. Ainda não foi contatado." },
  { id: "abordagem", label: "Abordagem", color: "#6366f1", help: "Primeiro contato feito. Explique como funciona a rede, os atendimentos e o repasse financeiro." },
  { id: "documentacao", label: "Documentação", color: "#f59e0b", help: "Parceiro tem interesse. Solicite a documentação: CNPJ, alvará, licenças, dados bancários." },
  { id: "validacao", label: "Validação", color: "#3b82f6", help: "Documentação enviada. Setor responsável está validando o cadastro." },
  { id: "ativacao", label: "Ativação/Treino", color: "#0d9488", help: "Cadastro aprovado! Repasse ao Suporte para treinamento no sistema e ativação." },
  { id: "ativo", label: "Ativo na Rede", color: "#059669", help: "Parceiro credenciado e operando. Mantenha relacionamento periódico." },
  { id: "sem_interesse", label: "Sem Interesse", color: "#d97706", help: "Parceiro sem interesse no momento. Fica fora do fluxo e reaparece para novo contato na data de retomada." },
  { id: "recusado", label: "Recusado", color: "#ef4444", help: "Documentação reprovada ou parceiro desistiu definitivamente. Registre o motivo." },
];

// ═══════════════════════════════════════════════════════════════
// FLUXO ÚNICO DA REDE — o estágio é consequência do que foi FEITO
// ═══════════════════════════════════════════════════════════════
//
// Antes havia um botão "avançar estágio" solto: dava para empurrar a empresa
// para "abordagem" sem ter falado com ninguém, e o funil passava a contar
// conversas que não existiram. Aqui o estágio não se escolhe — ele cai por
// consequência do ato registrado:
//
//   cadastrou            → identificado
//   registrou contato    → abordagem
//   ele aceitou          → documentação  (é onde a ficha e os links entram)
//   documentos completos → validação → ativo
//   disse que não        → sem interesse (com MOTIVO, sempre)
//
// Cada estágio declara o que se pode fazer nele. A tela lê daqui: sem esta
// lista, cada card decidiria sozinho quais botões mostrar, e a regra viraria
// aparência.
// As DECISÕES que cabem em cada estágio. A ficha não está aqui de propósito:
// ela aparece em todo estágio, sempre. Prender o botão da ficha ao estágio foi
// erro meu na unificação — a empresa em abordagem que já tinha ficha ficava sem
// nenhum jeito de abri-la pela Rede, que é justamente o que a aba prometia
// resolver.
export const ACOES_REDE = {
  identificado: ["contato", "sem_interesse"],
  abordagem: ["aceitou", "novo_contato", "sem_interesse"],
  // Aceitou e sumiu na documentação também é um "não" — antes só dava para
  // registrar recusa antes do aceite, e essas empresas ficavam encalhadas.
  documentacao: ["novo_contato", "sem_interesse"],
  validacao: [],
  ativacao: [],
  ativo: ["novo_contato"],
  sem_interesse: ["retomar"],
  recusado: ["retomar"],
};

export const acoesDoEstagio = (estagio) => ACOES_REDE[estagio] || ACOES_REDE.identificado;

// O que a ficha de credenciamento significa para o estágio da rede. Serve para
// as empresas que entraram pelo link (existe ficha, não existe parceiro): sem
// isto elas apareceriam como "identificado", como se ninguém tivesse falado
// com elas — quando na verdade já assinaram.
// Vinculada ao módulo (algum sistema devolveu provider_id) = operando na rede.
// Vale para a ficha que nasceu SÓ no CRM (sem parceiro): o avanço automático do
// funil pula quem não tem parceiro, então sem isto ela ficava presa em
// "validação" mesmo já cadastrada no módulo.
const fichaVinculadaAoModulo = (ficha) => {
  const sync = ficha?.integracaoSync && typeof ficha.integracaoSync === "object" ? ficha.integracaoSync : {};
  return Object.values(sync).some((v) => v && typeof v === "object" && (v.provider_id || v.providerId));
};

// A ficha tem aceite: termos aceitos, rubrica antiga, ou nome + documento de
// quem assina. Mesmo critério de `temAssinatura` (utils) — escrito aqui, e não
// importado de lá, porque utils importa este arquivo e importar de volta
// fecharia um ciclo.
export const fichaAssinada = (ficha) => !!ficha && (
  !!ficha.aceiteTermos || !!ficha.assinaturaPath
  || (!!ficha.responsavelNome && (!!ficha.responsavelDoc || !!ficha.responsavelRg || (ficha.documentos || []).length > 0))
);

// A ficha foi de fato ENVIADA: o fornecedor preencheu pelo link, ou ela foi
// assinada (na visita, ou o registro rápido que ele completou pelo link).
// É o que separa "Em documentação" de "aceitou, mas a ficha ainda não veio".
// A ficha foi CONFIRMADA: enviada e concluída (o fornecedor enviou pelo link,
// ou a equipe concluiu a visita), com o aceite/assinatura registrado. É o
// portão do vínculo com os módulos — o lote de 18/09 levou fichas ainda
// "pendente" porque a regra aceitava "tem nome + algum documento".
export const fichaConfirmada = (ficha) => !!ficha
  && ficha.status === "concluido"
  && (!!ficha.aceiteTermos || !!ficha.assinaturaPath);

export const fichaEnviada = (ficha) => !!ficha && (ficha.origem === "link" || fichaAssinada(ficha));

export const estagioPelaFicha = (ficha) => {
  if (!ficha) return "identificado";
  if (ficha.status === "sem_interesse") return "sem_interesse";
  if (fichaVinculadaAoModulo(ficha)) return "ativo";
  if (ficha.status === "concluido") return "validacao";
  // Registro rápido de contato (origem "contato", ainda pendente): a equipe só
  // anotou o contato inicial — ninguém falou com a empresa, nada foi assinado.
  // O lugar dela é "a contatar", não "documentação". A regra de documentação
  // existe para a ficha do LINK, que chega JÁ assinada; carimbar o contato
  // rápido do mesmo jeito mandava para a esteira de documentos uma empresa que
  // ainda precisa receber a primeira mensagem.
  //
  // Só enquanto ela continuar sendo isso: assim que o fornecedor preenche o
  // formulário pelo link (aceite dos termos, documento de quem assina), a MESMA
  // ficha deixa de ser um contato anotado e vira cadastro em andamento. Sem a
  // segunda condição, a empresa que acabou de se cadastrar voltava para "a
  // contatar" — e a equipe ligava de novo para quem já tinha assinado.
  if (ficha.origem === "contato" && ficha.status === "pendente" && !fichaAssinada(ficha)) return "identificado";
  return "documentacao";
};

export const PARTNER_TYPES = [
  { id: "oficina", label: "Oficina Mecânica", icon: "", help: "Oficinas que fazem manutenção preventiva e corretiva em veículos da frota dos clientes." },
  { id: "funilaria", label: "Funilaria / Pintura", icon: "", help: "Funilarias para reparos de lataria e pintura em veículos sinistrados." },
  { id: "borracharia", label: "Borracharia", icon: "", help: "Borracharias para troca, conserto e rodízio de pneus." },
  { id: "posto", label: "Posto de Combustível", icon: "", help: "Postos para abastecimento da frota. Opera com tabela ANP e comissão por litro." },
  { id: "autopeca", label: "Autopeça", icon: "", help: "Fornecedor de peças e componentes automotivos." },
  { id: "eletrica", label: "Elétrica Automotiva", icon: "", help: "Oficinas especializadas em parte elétrica e eletrônica veicular." },
];

export const FLEET_SIZES = [
  { id: "1-10", label: "1 a 10 veículos", help: "Micro empresa. Geralmente decisão rápida do dono." },
  { id: "11-50", label: "11 a 50 veículos", help: "Pequena empresa. Pode ter um gestor de frota dedicado." },
  { id: "51-200", label: "51 a 200 veículos", help: "Média empresa. Precisa de gestão profissional. Bom fit!" },
  { id: "201-500", label: "201 a 500 veículos", help: "Grande empresa. Negociação mais longa, geralmente com procurement." },
  { id: "500+", label: "500+ veículos", help: "Enterprise. Informe a Gerente de Operações — negociação estratégica." },
];

export const VEHICLE_TYPES = ["Utilitários", "Caminhões", "Vans", "Pickups", "Motos", "Ônibus", "Máquinas", "Misto"];

// ═══════════════════════════════════════════════════════════════
// CREDENCIAMENTO / FICHAS CADASTRAIS
// ═══════════════════════════════════════════════════════════════

// Sistemas em que a ficha/parceiro pode aparecer
export const SISTEMAS = [
  { id: "manutencao", label: "Manutenção", icon: "", color: "#7c3aed" },
  { id: "combustivel", label: "Combustível", icon: "", color: "#0d9488" },
  { id: "rastreamento", label: "Rastreamento", icon: "", color: "#2563eb" },
];

// Documentos do credenciamento, em DUAS etapas — e a divisão é o fluxo.
//
// Etapa 1 (assinatura): só o documento de QUEM assina a ficha e aceita os
// termos. É a prova de quem assinou — por isso é o único que trava.
//
// Etapa 2 (habilitação): o documento do representante da empresa, com a função
// dele. É pedido DEPOIS de cadastrar, pelo link de documentos: cobra, aparece
// como pendência, mas não segura o cadastro nem o aceite. Era exatamente aqui
// que o fluxo parava — a visita acabava esperando o fornecedor achar quatro
// papéis para conseguir cadastrar um.
//
// O `tipo` continua sendo o que a manutenção/combustível esperam, então cada
// arquivo entra lá contando como o documento certo e ninguém reenvia.
export const DOC_ASSINANTE = {
  tipo: "documento_responsavel",
  label: "Documento de quem assina",
  dica: "RG, CNH ou CPF de quem assina a ficha e aceita os termos",
};
export const DOCS_COMPLEMENTARES = [
  { tipo: "documento_representante", label: "Documento do representante da empresa", dica: "RG ou CNH do sócio/representante legal — informe também a função dele" },
];

// ── O que o fornecedor NÃO manda mais ──
//
// Estes três continuam existindo na ficha e nos sistemas; o que mudou é a
// origem. Se o dado vem da fonte oficial pelo CNPJ, pedir a foto do papel é
// pedir que o fornecedor vá buscar o que já está do nosso lado.
//
//   Cartão CNPJ         → emitido na Receita (api/cnpj-comprovante.js) e
//                         anexado sozinho, com `tipo: "cnpj"`. É o documento de
//                         verdade, não um modelo nosso.
//   Contrato social     → o que se lia nele — quem responde pela empresa — vem
//                         no quadro societário da consulta por CNPJ, que chega
//                         junto do Cartão CNPJ. Pedir o contrato era pedir a
//                         foto de um papel para saber o que já se sabe, e era
//                         o documento que mais atrasava habilitação: o
//                         fornecedor tem de achar a última alteração
//                         consolidada, muitas vezes só com o contador.
//   Comprovante de end. → o endereço fiscal está no próprio Cartão CNPJ.
//   Certificado ANP     → o registro do posto vem da consulta à ANP (lib/anp.js)
//                         e viaja no perfil da ficha.
//
// Ficam catalogados para rotular o que já foi enviado (ou emitido) — sem isso
// o arquivo apareceria como "outro anexo", sem nome.
export const DOC_CARTAO_CNPJ = { tipo: "cnpj", label: "Cartão CNPJ", dica: "Emitido na Receita Federal pelo CNPJ — o fornecedor não envia" };
export const DOC_CONTRATO_SOCIAL = { tipo: "contrato_social", label: "Contrato Social", dica: "Dispensado — o quadro societário vem na consulta por CNPJ" };
export const DOC_COMPROVANTE_ENDERECO = { tipo: "comprovante_endereco", label: "Comprovante de Endereço", dica: "Dispensado — o endereço fiscal consta no Cartão CNPJ" };
export const DOC_ANP_POSTO = { tipo: "certificado_anp", label: "Certificado de Registro ANP", dica: "Dispensado — o registro vem direto da ANP" };

export const DOCS_AUTOMATICOS = [DOC_CARTAO_CNPJ, DOC_CONTRATO_SOCIAL, DOC_COMPROVANTE_ENDERECO, DOC_ANP_POSTO];

// Oficina aeronáutica: o que prova a certificação. Pedidos na 2ª etapa, como os
// outros complementares — cobram, não seguram o cadastro. No sistema de
// Manutenção são estes papéis que a equipe confere antes de a oficina ficar
// elegível para tarefa aeronáutica.
export const DOC_COM_ANAC = { tipo: "certificado_com", label: "Certificado de Organização de Manutenção (COM)", dica: "Exigência ANAC — RBAC 145.5. Cópia do COM emitido pela ANAC" };
export const DOC_EO_ANAC = { tipo: "especificacoes_operativas", label: "Especificações Operativas (EO) e Lista de Capacidade", dica: "Exigência ANAC — RBAC 145.53 e 145.215-I. Revisão vigente" };
export const DOC_LICENCA_MMA = { tipo: "licenca_mma", label: "Licença / CHT do mecânico (MMA)", dica: "Exigência ANAC — RBAC 65. CHT Digital com as habilitações" };
export const DOCS_AERONAUTICOS = [DOC_COM_ANAC, DOC_EO_ANAC, DOC_LICENCA_MMA];

export const DOCS_CREDENCIAMENTO = [DOC_ASSINANTE, ...DOCS_COMPLEMENTARES];
// Todo tipo que a ficha sabe nomear — pedido, automático ou herdado.
export const CATALOGO_DOCS = [...DOCS_CREDENCIAMENTO, ...DOCS_AUTOMATICOS, ...DOCS_AERONAUTICOS];

// Quais documentos aeronáuticos esta ficha deve: COM + EO para quem é (ou está
// virando) organização certificada; a licença para quem trabalha pelo mecânico.
const docsAeronauticosDa = (ficha) => {
  const tipo = ficha?.tipoEstabelecimento || ficha?.tipo_estabelecimento;
  if (tipo !== "oficina_aeronautica") return [];
  const sit = ficha?.perfil?.situacao_anac;
  if (sit === "certificada_145" || sit === "em_certificacao") return [DOC_COM_ANAC, DOC_EO_ANAC];
  if (sit === "mma_43") return [DOC_LICENCA_MMA];
  return [];
};

// Documentos de uma etapa. `docs` é o que a ficha já tem: os automáticos e os
// herdados entram na lista quando existem, para aparecerem com nome e não como
// anexo solto.
export const docsDaFicha = (etapa = "todos", docs = [], ficha = null) => {
  const base = etapa === "assinatura" ? [DOC_ASSINANTE]
    : etapa === "complementares" ? docsPedidos(ficha)
    : [DOC_ASSINANTE, ...docsPedidos(ficha)];
  if (etapa === "assinatura") return base;
  const presentes = new Set((docs || []).map((d) => d?.tipo));
  return [...base, ...DOCS_AUTOMATICOS.filter((t) => presentes.has(t.tipo))];
};

// Já veio o documento de quem assinou? É o que trava (e o que destrava).
export const temDocAssinante = (docs = []) => (docs || []).some((d) => d?.tipo === DOC_ASSINANTE.tipo);

// ── Quem assinou já é o representante legal? ──
//
// Na maioria das fichas é a mesma pessoa: quem assina é o sócio-administrador.
// Aí o "documento do representante" é a MESMA foto que ele mandou na
// assinatura — e pedir de novo é mandar o fornecedor fotografar o RG duas
// vezes, com semanas de diferença, para o mesmo cadastro.
//
// A comparação é por nome porque é o que se tem dos dois lados: o nome que ele
// digitou e o do administrador que veio no quadro societário do CNPJ. Conectivo
// ("da", "de", "dos") não conta, e nome abreviado casa com o completo — "João
// Silva" é o mesmo "João da Silva Santos" para este fim. Errar para o lado de
// NÃO pedir é o certo aqui: na dúvida, quem assinou responde pela empresa.
const semAcento = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
const CONECTIVOS = new Set(["de", "da", "do", "das", "dos", "e"]);
const nomeEmPartes = (s) => semAcento(s).split(/\s+/).filter((t) => t && !CONECTIVOS.has(t));

export const mesmaPessoa = (a, b) => {
  const ta = nomeEmPartes(a), tb = nomeEmPartes(b);
  if (!ta.length || !tb.length) return false;
  const [curto, longo] = ta.length <= tb.length ? [ta, tb] : [tb, ta];
  return curto.every((t) => longo.includes(t));
};

// Sem representante conhecido, quem assinou é quem responde pela empresa —
// então também não há segundo documento a pedir.
export const assinanteEhRepresentante = (ficha = {}) =>
  !String(ficha?.representanteNome || "").trim() ||
  mesmaPessoa(ficha?.responsavelNome, ficha?.representanteNome);

// Os complementares que ainda faltam — pendência que cobra, não que bloqueia.
// Com a ficha em mãos, o documento do representante some quando ele já é quem
// assinou.
export const docsComplementaresFaltando = (docs = [], ficha = null) => {
  const presentes = new Set((docs || []).map((d) => d?.tipo));
  return docsPedidos(ficha).filter((t) => !presentes.has(t.tipo));
};

// O que ainda se pede ao fornecedor na 2ª etapa, para esta ficha.
export const docsPedidos = (ficha = null) => [
  ...DOCS_COMPLEMENTARES.filter((t) => !(t.tipo === "documento_representante" && ficha && assinanteEhRepresentante(ficha))),
  ...docsAeronauticosDa(ficha),
];

// ── O fornecedor pode ter mandado direto no sistema ──
//
// O link de documentos da manutenção/combustível é dele: ele envia por lá e o
// CRM não fica sabendo — a lista `documentos` daqui não muda. Sem isto, a ficha
// continuaria dizendo "pedir contrato social" para quem já tinha entregado, e
// alguém mandaria a cobrança.
//
// Quem sabe a verdade é o sistema, e ele responde na consulta por CNPJ:
// `docs_faltantes` é o que ainda falta LÁ. Um obrigatório que não está nessa
// lista é um documento que chegou.
// Só o que ainda é cobrado LÁ. Cartão CNPJ e contrato social saíram dos
// obrigatórios nos três sistemas — deixá-los aqui faria a ficha marcar como
// "entregue no sistema" um documento que ninguém mais pede, e o rótulo diria
// uma coisa que não aconteceu.
const TIPOS_COBRADOS_NOS_SISTEMAS = ["documento_responsavel"];
const tipoDoSistema = (t) => (t === "cartao_cnpj" ? "cnpj" : t);

export const docsEntreguesNoSistema = (dados) => {
  if (!dados?.found || !Array.isArray(dados.docs_faltantes)) return [];
  const faltamLa = new Set(dados.docs_faltantes.map((d) => tipoDoSistema(d?.tipo)));
  return TIPOS_COBRADOS_NOS_SISTEMAS.filter((t) => !faltamLa.has(t));
};

// Tipo de estabelecimento credenciado (não é só oficina)
export const TIPOS_ESTABELECIMENTO = [
  { id: "oficina", label: "Oficina Mecânica" },
  { id: "autopecas", label: "Autopeças (loja)" },
  { id: "pecas_pesadas", label: "Comércio de Peças Pesadas" },
  { id: "loja_pecas", label: "Loja de Peças / Comércio" },
  { id: "funilaria", label: "Funilaria / Pintura" },
  { id: "borracharia", label: "Borracharia / Pneus" },
  { id: "eletrica", label: "Elétrica Automotiva" },
  { id: "posto", label: "Posto de Combustível" },
  { id: "guincho", label: "Guincho / Reboque" },
  { id: "oficina_aeronautica", label: "Oficina de Manutenção Aeronáutica (RBAC 145)" },
  { id: "outro", label: "Outro" },
];

// Tipos de veículo que o estabelecimento atende
export const TIPOS_VEICULO = [
  { id: "motos", label: "Motos" },
  { id: "carros", label: "Carros / Leves" },
  { id: "vans", label: "Camionetes / Vans" },
  { id: "caminhoes", label: "Caminhões" },
  { id: "onibus", label: "Ônibus" },
  { id: "maquinas", label: "Máquinas" },
  { id: "barcos", label: "Barcos / Náutica" },
];

// Especialidades agrupadas por categoria de veículo.
// "geral" e "comercio" são sempre exibidos; os demais aparecem conforme os
// tipos de veículo marcados. Cada item tem id único (mantém histórico salvo).
export const SPECIALTY_GROUPS = [
  {
    veic: "geral", cat: "Geral / Compartilhado",
    items: [
      { id: "revisao", label: "Revisão / Troca de óleo" },
      { id: "motor", label: "Motor (reparo)" },
      { id: "retifica", label: "Retífica de motores" },
      { id: "injecao", label: "Injeção eletrônica" },
      { id: "diagnostico", label: "Diagnóstico eletrônico (scanner)" },
      { id: "cambio", label: "Câmbio / Transmissão" },
      { id: "cambio_auto", label: "Câmbio automático" },
      { id: "embreagem", label: "Embreagem" },
      { id: "freios", label: "Freios" },
      { id: "suspensao", label: "Suspensão" },
      { id: "direcao", label: "Direção (hidráulica/elétrica)" },
      { id: "eletrica", label: "Sistema elétrico" },
      { id: "arrefecimento", label: "Arrefecimento / Radiador" },
      { id: "ar_condicionado", label: "Ar-condicionado" },
      { id: "escapamento", label: "Escapamento" },
      { id: "alinhamento", label: "Alinhamento e balanceamento" },
      { id: "pneus", label: "Pneus / Borracharia" },
      { id: "funilaria", label: "Funilaria e pintura" },
      { id: "vidros", label: "Vidros / Tapeçaria" },
      { id: "baterias", label: "Baterias (serviço)" },
      { id: "solda", label: "Solda / Caldeiraria" },
      { id: "lavagem", label: "Lavagem / Higienização" },
      { id: "rastreador_inst", label: "Instalação de rastreador" },
      { id: "som", label: "Som / Multimídia / Acessórios" },
      { id: "guincho", label: "Guincho / Socorro" },
    ],
  },
  {
    veic: "motos", cat: "Motos",
    items: [
      { id: "moto_motor", label: "Motor de motos (2T/4T)" },
      { id: "moto_injecao", label: "Injeção / Carburação de moto" },
      { id: "moto_transmissao", label: "Relação / Corrente / Transmissão" },
      { id: "moto_freios", label: "Freios de moto" },
      { id: "moto_suspensao", label: "Suspensão de moto" },
      { id: "moto_eletrica", label: "Elétrica / CDI de moto" },
      { id: "moto_pneus", label: "Pneus de moto" },
    ],
  },
  {
    veic: "carros", cat: "Carros / Leves",
    items: [
      { id: "carro_hibrido", label: "Híbridos / Elétricos" },
      { id: "carro_multimidia", label: "Central multimídia" },
      { id: "carro_vidros_eletricos", label: "Vidros / Travas elétricas" },
      { id: "carro_blindagem", label: "Blindagem / Vidros blindados" },
    ],
  },
  {
    veic: "vans", cat: "Camionetes / Vans / Utilitários",
    items: [
      { id: "van_diesel", label: "Diesel leve / Utilitário" },
      { id: "van_bau", label: "Baú / Furgão" },
      { id: "van_camara_fria", label: "Câmara fria / Refrigerada" },
      { id: "van_suspensao", label: "Suspensão reforçada" },
      { id: "van_transformacao", label: "Transformação / Acessibilidade" },
      { id: "van_freios", label: "Freios (utilitário)" },
    ],
  },
  {
    veic: "caminhoes", cat: "Caminhões",
    items: [
      { id: "diesel", label: "Elétrica diesel / Bombas injetoras" },
      { id: "injecao_diesel", label: "Injeção diesel / Common rail" },
      { id: "turbo", label: "Turbo / Turbocompressor" },
      { id: "molas", label: "Molas / Feixe de molas" },
      { id: "cam_freio_ar", label: "Freio a ar / Sistema pneumático" },
      { id: "cam_quinta_roda", label: "Quinta roda / Engate" },
      { id: "cam_diferencial", label: "Diferencial / Cardã" },
      { id: "cam_carroceria", label: "Baú / Carroceria" },
      { id: "tacografo", label: "Tacógrafo" },
    ],
  },
  {
    veic: "onibus", cat: "Ônibus",
    items: [
      { id: "onibus_ar", label: "Ar-condicionado de ônibus" },
      { id: "onibus_porta", label: "Portas pneumáticas" },
      { id: "onibus_suspensao_ar", label: "Suspensão a ar / Bolsa" },
      { id: "onibus_poltronas", label: "Poltronas / Interior" },
      { id: "onibus_elevador", label: "Elevador de acessibilidade" },
    ],
  },
  {
    veic: "maquinas", cat: "Máquinas (Agrícolas/Pesadas)",
    items: [
      { id: "hidraulica", label: "Sistema hidráulico" },
      { id: "maq_motor_diesel", label: "Motor diesel de máquinas" },
      { id: "maq_transmissao", label: "Transmissão / Powershift" },
      { id: "maq_esteira", label: "Material rodante / Esteiras" },
      { id: "maq_implementos", label: "Implementos agrícolas" },
      { id: "maq_ar_cabine", label: "Ar-condicionado de cabine" },
    ],
  },
  {
    veic: "barcos", cat: "Barcos / Náutica",
    items: [
      { id: "barco_popa", label: "Motor de popa" },
      { id: "barco_centro", label: "Motor de centro / Rabeta" },
      { id: "barco_casco", label: "Casco / Fibra / Gelcoat" },
      { id: "barco_eletrica", label: "Elétrica náutica" },
      { id: "barco_hidraulico", label: "Hidráulico / Direção náutica" },
      { id: "barco_reboque", label: "Carreta / Reboque náutico" },
    ],
  },
  {
    veic: "comercio", cat: "Comércio de Peças",
    items: [
      { id: "venda_pecas_leve", label: "Peças — linha leve" },
      { id: "venda_pecas_pesada", label: "Peças — linha pesada" },
      { id: "venda_pecas_agri", label: "Peças agrícolas" },
      { id: "venda_pecas_moto", label: "Peças de motos" },
      { id: "venda_pecas_nautica", label: "Peças náuticas" },
      { id: "filtros_lubrif", label: "Filtros e lubrificantes" },
      { id: "oleos", label: "Óleos e fluidos" },
      { id: "venda_pneus", label: "Venda de pneus" },
      { id: "venda_baterias", label: "Venda de baterias" },
      { id: "rolamentos", label: "Rolamentos e correias" },
      { id: "ferramentas", label: "Ferramentas e equipamentos" },
      { id: "acessorios", label: "Acessórios automotivos" },
    ],
  },
];

// Lista achatada (usada para lookup de labels em qualquer lugar)
export const MECH_SPECIALTIES = SPECIALTY_GROUPS.flatMap((g) => g.items);

// ── Serviços prestados ──────────────────────────────────────────────
// ESPELHO da tabela `provider_service_types` do sistema de MANUTENÇÃO.
// O `id` é o MESMO id do registro lá e o `label` é o texto exato — assim, ao
// terminar a ficha, o que o parceiro marcou casa 1:1 com o cadastro do sistema
// (nada de "traduzir" nome na mão na hora de credenciar).
// Ao criar/renomear um serviço lá, replique aqui mantendo o id.
//
// A fonte é `db/seeds/populate_default.rb` do repo de manutenção
// (sistema-insta-solutions-frota), que se declara espelho de produção. O teste
// `scripts/teste-contrato-servicos.mjs` compara esta lista com ele e reprova
// qualquer diferença de id ou de nome.
// 21/10/2026: a manutenção ganhou os três tipos aeronáuticos (migração
// 20261001183000) e esta cópia tinha parado no 32. Conferido em produção, só
// leitura: 35 tipos, 33/34/35 = os aeronáuticos. Eles ficam marcados
// `aeronautico` e não aparecem para oficina terrestre (SERVICOS_TERRESTRES).
export const SERVICOS_PRESTADOS = [
  { id: 1, label: "Serviço de tacógrafos" },
  { id: 2, label: "Aquisição de peças" },
  { id: 3, label: "Lubrificantes e filtros" },
  { id: 4, label: "Solicitação de guincho" },
  { id: 5, label: "Serviços de mecânica com peças" },
  { id: 6, label: "Aquisição de pneu" },
  { id: 7, label: "Reforma de pneu" },
  { id: 8, label: "Tornearia" },
  { id: 9, label: "Solda em geral" },
  { id: 10, label: "Borracharia" },
  { id: 11, label: "Tapeçaria" },
  { id: 12, label: "Funilaria e pintura" },
  { id: 13, label: "Inspeção veicular" },
  { id: 14, label: "Vistoria veicular" },
  { id: 15, label: "Garantia" },
  { id: 16, label: "Serviço de elétrica" },
  { id: 17, label: "Serviço de radiador" },
  { id: 18, label: "Serviço de portas e acessórios" },
  { id: 19, label: "Para-brisas" },
  { id: 20, label: "Diagnóstico" },
  { id: 21, label: "Despachante" },
  { id: 22, label: "Ar-condicionado" },
  { id: 23, label: "Serviços mecânicos" },
  { id: 24, label: "Serviço de moto" },
  { id: 25, label: "Lavagem automotiva" },
  { id: 26, label: "Vitrificação" },
  { id: 27, label: "Comércio de placas" },
  { id: 28, label: "Reparos Hidráulicos" },
  { id: 29, label: "Manutenção de máquinas pesadas" },
  { id: 30, label: "Revisão periódica" },
  { id: 31, label: "Gráfica/Plotagem/Adesivos" },
  { id: 32, label: "Serviço de molas" },
  { id: 33, label: "Serviços aeronáuticos", aeronautico: true },
  { id: 34, label: "Inspeção aeronáutica", aeronautico: true },
  { id: 35, label: "Revisão aeronáutica", aeronautico: true },
];

// O que se oferece a oficina, posto, guincho e borracharia.
export const SERVICOS_TERRESTRES = SERVICOS_PRESTADOS.filter((s) => !s.aeronautico);

export const servicoLabel = (id) => SERVICOS_PRESTADOS.find((s) => s.id === id)?.label || "";

// ── Socorro 24h / assistência em rodovias ───────────────────────────
// Alimenta a rede de assistência do sistema de RASTREAMENTO.
// Os `id` são os MESMOS códigos usados em `pricing` do Partner de assistência lá
// (towing/tire/battery/locksmith/fuel/mechanical) — ver FichaDoPrestador.tsx no
// instafrota. Assim o que o parceiro marca aqui entra direto na rede, sem de-para.
export const ASSISTENCIA_TIPOS = [
  { id: "towing", label: "Reboque / Guincho" },
  { id: "fuel", label: "Pane seca (combustível)" },
  { id: "tire", label: "Troca de pneu / Borracharia" },
  { id: "battery", label: "Elétrica / Bateria" },
  { id: "mechanical", label: "Socorro mecânico no local" },
  { id: "locksmith", label: "Chaveiro" },
];

// ── Perfil por ramo (posto, guincho, borracharia…) ──────────────────
// A ficha era uma só: quem preenchia por um posto via "tipos de veículo
// atendidos" e nenhuma pergunta sobre bandeira ou combustível. Aqui cada ramo
// declara o que só ele tem, e o formulário monta a partir disso — nada de `if`
// espalhado pelas telas. Tudo o que for marcado cai no JSONB `perfil`.

export const BANDEIRAS_POSTO = [
  { id: "vibra", label: "Vibra / BR (Petrobras)" },
  { id: "ipiranga", label: "Ipiranga" },
  { id: "shell", label: "Shell (Raízen)" },
  { id: "ale", label: "Ale" },
  { id: "sim", label: "SIM" },
  { id: "rodoil", label: "RodoOil" },
  { id: "petrox", label: "Petrox" },
  { id: "sabba", label: "Sabbá" },
  { id: "charrua", label: "Charrua" },
  { id: "federal", label: "Federal" },
  { id: "total", label: "TotalEnergies" },
  { id: "branca", label: "Bandeira branca (sem bandeira)" },
  { id: "outra", label: "Outra" },
];

// Rótulos iguais aos de PRODUTOS da Ficha Cadastral (Anexo II) do sistema de
// Combustível (src/services/credenciamento/campos.js). Os ids continuam os
// daqui — já estão gravados em fichas e o outro lado guarda o perfil como JSON.
export const COMBUSTIVEIS = [
  { id: "gasolina_comum", label: "Gasolina Comum" },
  { id: "gasolina_aditivada", label: "Gasolina Aditivada" },
  { id: "gasolina_premium", label: "Gasolina Premium" },
  { id: "etanol", label: "Etanol Hidratado" },
  { id: "etanol_aditivado", label: "Etanol Aditivado" },
  { id: "diesel_s10", label: "Diesel S-10" },
  { id: "diesel_s500", label: "Diesel S-500" },
  { id: "diesel_aditivado", label: "Diesel Aditivado" },
  { id: "gnv", label: "GNV" },
  { id: "arla", label: "ARLA 32 (bomba)" },
];

const SIM_NAO = [{ id: "sim", label: "Sim" }, { id: "nao", label: "Não" }];

// TU-COMB-3.1, Cl. 8.2 — a taxa é um percentual sobre o valor faturado (preço
// do posto × litros) e sai do prazo de repasse que o próprio posto escolhe.
// Substitui a taxa fixa por litro (R$ 0,08–0,15) do TU-COMB-2.0.
export const PRAZOS_REPASSE = [
  { id: "semanal", label: "Semanal — taxa de 2,5%", taxa: 2.5, dias: 7 },
  { id: "quinzenal", label: "Quinzenal — taxa de 2,0%", taxa: 2.0, dias: 15 },
  { id: "mensal", label: "Mensal — taxa de 1,6%", taxa: 1.6, dias: 30 },
];
const numeroBR = (v) => {
  const n = parseFloat(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
};
const pctBR = (n) => `${String(n).replace(".", ",")}%`;
const reaisBR = (n) => `R$ ${Number(n).toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;

// ── TABELAS COMERCIAIS DO COMBUSTÍVEL ────────────────────────────────────────
//
// Um termo só (as regras) e uma TABELA por posto (os números): taxa por prazo,
// os prazos oferecidos, aluguel e isenção da maquininha e a forma de
// pagamento do repasse. O comercial cria as tabelas (tabela
// `tabelas_comerciais`) e escolhe uma por ficha.
//
// A ficha guarda uma CÓPIA da tabela em `perfil.tabelaComercial` — é o que o
// posto vê no formulário e aceita. Mudar a tabela depois não mexe em ficha
// nenhuma: o que foi aceito fica (R-127). Sem cópia, vale a TABELA_PADRAO, que
// é a do TU-COMB-3.1 — ficha antiga continua exatamente como era.
//
// Quem garante que o posto não troca a própria tabela é o banco (gatilho
// `condicoes_comerciais_do_posto`): o anônimo não grava `tabelaComercial`; ela
// só chega pelo convite que o comercial gerou ao mandar o link.
export const FORMAS_PAGAMENTO = [
  { id: "pix", label: "PIX na chave do posto (mesmo CNPJ)" },
  { id: "ted", label: "TED / transferência na conta indicada (mesmo CNPJ)" },
];

export const TABELA_PADRAO = {
  codigo: "PADRAO",
  nome: "Tabela padrão (TU-COMB-3.1)",
  prazos: PRAZOS_REPASSE.map(({ id, taxa, dias }) => ({ id, nome: id[0].toUpperCase() + id.slice(1), dias, taxa })),
  aluguelMensal: 4.99,
  isencaoAcima: 10000,
  formasPagamento: [],
};

// Prazo com nome: os três de sempre mantêm o id que o combustível já conhece.
const NOME_PRAZO = { 7: "Semanal", 15: "Quinzenal", 30: "Mensal" };
const ID_PRAZO = { 7: "semanal", 15: "quinzenal", 30: "mensal" };
export const idDoPrazo = (dias) => ID_PRAZO[dias] || `d${dias}`;
export const nomeDoPrazo = (dias) => NOME_PRAZO[dias] || `${dias} dias`;
// "Mensal (30 dias)", mas "45 dias" — sem repetir o número.
const rotuloPrazo = (p) => (NOME_PRAZO[p.dias] ? `${NOME_PRAZO[p.dias]} (${p.dias} dias)` : `${p.dias} dias`);

/** A tabela que vale para esta ficha: a cópia aceita, ou a padrão. */
export const tabelaDaFicha = (perfil) => {
  const t = perfil?.tabelaComercial;
  return t && Array.isArray(t.prazos) && t.prazos.length ? t : TABELA_PADRAO;
};

export const prazosDaFicha = (perfil) => tabelaDaFicha(perfil).prazos.map((p) => ({
  id: p.id, dias: p.dias, taxa: p.taxa,
  label: `${rotuloPrazo(p)} — taxa de ${pctBR(p.taxa)}`,
}));

/** Taxa do prazo na tabela da ficha. Sem perfil, a padrão (compatível com o uso antigo). */
export const taxaDoPrazo = (prazo, perfil) => tabelaDaFicha(perfil).prazos.find((p) => p.id === prazo)?.taxa ?? null;
export const diasDoPrazo = (prazo, perfil) => tabelaDaFicha(perfil).prazos.find((p) => p.id === prazo)?.dias ?? null;

export const formasDaFicha = (perfil) => {
  const ids = tabelaDaFicha(perfil).formasPagamento || [];
  return FORMAS_PAGAMENTO.filter((f) => ids.includes(f.id));
};

/**
 * A linha do banco (`tabelas_comerciais`) vira a cópia que vai para a ficha.
 * Só os números que o posto aceita — nada de quem criou ou quando ativou.
 */
export function copiaDaTabela(linha) {
  if (!linha) return null;
  const prazos = (Array.isArray(linha.prazos) ? linha.prazos : [])
    .map((p) => ({ dias: Number(p.dias), taxa: numeroBR(p.taxa) }))
    .filter((p) => Number.isInteger(p.dias) && p.dias > 0 && p.taxa !== null)
    .sort((a, b) => a.dias - b.dias)
    .map((p) => ({ id: idDoPrazo(p.dias), nome: nomeDoPrazo(p.dias), dias: p.dias, taxa: p.taxa }));
  return {
    codigo: linha.codigo,
    nome: linha.nome,
    ...(ehExclusiva(linha) ? { exclusiva: true } : {}),
    versao: linha.atualizado_em || linha.atualizadoEm || linha.criado_em || linha.criadoEm || null,
    prazos,
    aluguelMensal: numeroBR(linha.aluguel_mensal ?? linha.aluguelMensal),
    isencaoAcima: numeroBR(linha.isencao_acima ?? linha.isencaoAcima),
    formasPagamento: (Array.isArray(linha.formas_pagamento ?? linha.formasPagamento) ? (linha.formas_pagamento ?? linha.formasPagamento) : [])
      .filter((id) => FORMAS_PAGAMENTO.some((f) => f.id === id)),
  };
}

/** O que impede de salvar uma tabela. [] = pode salvar. */
export function problemasDaTabela(t) {
  const erros = [];
  if (!String(t?.codigo || "").trim()) erros.push("Código");
  else if (!/^[A-Z0-9][A-Z0-9_-]{1,39}$/.test(String(t.codigo).trim())) erros.push("Código: só letras maiúsculas, números, - e _ (2 a 40)");
  else if (String(t.codigo).trim() === TABELA_PADRAO.codigo) erros.push("Código PADRAO é reservado");
  if (!String(t?.nome || "").trim()) erros.push("Nome");
  const prazos = Array.isArray(t?.prazos) ? t.prazos : [];
  if (!prazos.length) erros.push("Pelo menos um prazo de repasse");
  const dias = new Set();
  prazos.forEach((p, i) => {
    const d = Number(p.dias), x = numeroBR(p.taxa);
    if (!Number.isInteger(d) || d < 1 || d > 120) erros.push(`Prazo ${i + 1}: dias de 1 a 120`);
    if (x === null || x < 0 || x > 20) erros.push(`Prazo ${i + 1}: taxa de 0 a 20%`);
    if (dias.has(d)) erros.push(`Prazo ${i + 1}: ${d} dias repetido`);
    dias.add(d);
  });
  const aluguel = numeroBR(t?.aluguel_mensal ?? t?.aluguelMensal);
  if (aluguel === null || aluguel < 0) erros.push("Aluguel da maquininha (0 = sem aluguel)");
  const isencao = t?.isencao_acima ?? t?.isencaoAcima;
  if (String(isencao ?? "").trim() && (numeroBR(isencao) === null || numeroBR(isencao) < 0)) erros.push("Isenção: valor inválido");
  const formas = t?.formas_pagamento ?? t?.formasPagamento;
  if (!Array.isArray(formas) || !formas.length) erros.push("Pelo menos uma forma de pagamento");
  return erros;
}

// ── Condição EXCLUSIVA e estados com taxa definida no cadastro ──────────────
//
// Em alguns estados (lista no banco: `regioes_comerciais`, começou com RJ, ES,
// MG, GO, TO e BA em 29/10/2026) não há tabela padrão: a taxa de cada posto é
// negociada no cadastro e vale só para ele. A condição exclusiva tem a mesma
// forma de uma tabela, mas não vira linha em `tabelas_comerciais` — mora só na
// ficha (e no convite do link), então nenhum outro fornecedor a vê ou usa.
export const novoCodigoExclusivo = () => `EXC-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
export const ehExclusiva = (t) => !!t && (t.exclusiva === true || /^EXC-/.test(String(t.codigo || "")));

/** O estado do posto exige condição definida no cadastro e a ficha ainda não tem? */
export const condicoesADefinir = (estado, perfil, ufsExclusivas = []) =>
  ufsExclusivas.includes(String(estado || "").trim().toUpperCase()) && !(perfil?.tabelaComercial?.prazos || []).length;

/** O quadro em linhas legíveis — formulário, PDF e prévia. */
export function quadroDaTabela(t) {
  const tab = t || TABELA_PADRAO;
  const linhas = [];
  linhas.push(`${ehExclusiva(tab) ? "Condição comercial exclusiva deste posto" : "Tabela comercial"}: ${tab.nome}${tab.codigo ? ` (${tab.codigo})` : ""}`);
  linhas.push(`Prazos de repasse e taxa sobre o valor faturado: ${tab.prazos.map((p) => `${rotuloPrazo(p)} ${pctBR(p.taxa)}`).join("; ")}`);
  const aluguel = numeroBR(tab.aluguelMensal);
  linhas.push(aluguel ? `Maquininha da InstaSolutions (opcional): ${reaisBR(aluguel)} por mês, por equipamento${tab.isencaoAcima ? ` — isento no mês em que o posto faturar mais de ${reaisBR(tab.isencaoAcima)} pela plataforma` : ""}` : "Maquininha da InstaSolutions (opcional): sem aluguel");
  const formas = FORMAS_PAGAMENTO.filter((f) => (tab.formasPagamento || []).includes(f.id)).map((f) => f.label);
  linhas.push(`Pagamento do repasse: ${formas.length ? formas.join(" ou ") : "na conta bancária indicada pelo posto (mesmo CNPJ)"}`);
  return linhas;
}

export const SERVICOS_POSTO = [
  { id: "troca_oleo", label: "Troca de óleo" },
  { id: "filtros", label: "Troca de filtros" },
  { id: "lubrificacao", label: "Lubrificação / graxa" },
  { id: "suspensao", label: "Suspensão" },
  { id: "freios", label: "Freios" },
  { id: "alinhamento", label: "Alinhamento e balanceamento" },
  { id: "borracharia", label: "Borracharia / conserto de pneu" },
  { id: "calibragem", label: "Calibragem / nitrogênio" },
  { id: "eletrica", label: "Elétrica / bateria" },
  { id: "arrefecimento", label: "Radiador / arrefecimento" },
  { id: "ar_condicionado", label: "Ar-condicionado" },
  { id: "revisao", label: "Revisão periódica" },
  { id: "lavagem", label: "Lava-rápido / lavagem" },
  { id: "polimento", label: "Polimento / vitrificação" },
  { id: "guincho", label: "Socorro / guincho" },
  { id: "conveniencia", label: "Loja de conveniência" },
  { id: "restaurante", label: "Restaurante / lanchonete" },
  { id: "patio_caminhao", label: "Pátio / pernoite para caminhão" },
  { id: "ducha", label: "Banheiro / ducha para motorista" },
  { id: "balanca", label: "Balança" },
];

export const PRODUTOS_POSTO = [
  { id: "oleo_motor", label: "Óleos lubrificantes de motor" },
  { id: "oleo_cambio", label: "Óleo de câmbio / transmissão" },
  { id: "fluido_freio", label: "Fluido de freio" },
  { id: "aditivo_radiador", label: "Aditivo de radiador" },
  { id: "aditivo_combustivel", label: "Aditivo de combustível" },
  { id: "arla_galao", label: "Arla 32 em galão" },
  { id: "agua_desmineralizada", label: "Água desmineralizada" },
  { id: "filtros_venda", label: "Filtros (ar, óleo, combustível)" },
  { id: "palhetas", label: "Palhetas de limpador" },
  { id: "lampadas", label: "Lâmpadas" },
  { id: "baterias", label: "Baterias" },
  { id: "pneus", label: "Pneus" },
  { id: "aromatizantes", label: "Aromatizantes" },
  { id: "ceras", label: "Ceras e produtos de limpeza" },
  { id: "acessorios", label: "Acessórios automotivos" },
  { id: "gas", label: "Gás de cozinha" },
  { id: "conveniencia_itens", label: "Conveniência (bebidas e alimentos)" },
];

export const EQUIPAMENTOS_GUINCHO = [
  { id: "plataforma", label: "Plataforma / prancha" },
  { id: "asa_delta", label: "Asa delta / lança" },
  { id: "munck", label: "Munck" },
  { id: "reboque_leve", label: "Reboque leve" },
  { id: "prancha_pesada", label: "Prancha para pesados" },
  { id: "cegonha", label: "Cegonha" },
  { id: "moto", label: "Reboque de motos" },
  { id: "off_road", label: "Resgate 4x4 / off-road" },
];

export const CAPACIDADES_GUINCHO = [
  { id: "ate_3500", label: "Até 3,5 t (linha leve)" },
  { id: "ate_8000", label: "Até 8 t (utilitários)" },
  { id: "ate_15000", label: "Até 15 t (caminhão médio)" },
  { id: "acima_15000", label: "Acima de 15 t (pesados)" },
];

export const REMOCOES_GUINCHO = [
  { id: "sinistro", label: "Remoção de sinistro / pátio" },
  { id: "maquinas", label: "Máquinas e implementos" },
  { id: "blindados", label: "Veículos blindados" },
  { id: "motos", label: "Motocicletas" },
  { id: "onibus", label: "Ônibus" },
  { id: "embarcacoes", label: "Embarcações / náutica" },
  { id: "interestadual", label: "Transporte interestadual" },
  { id: "leilao", label: "Leilão / transferência de frota" },
];

export const LINHAS_BORRACHARIA = [
  { id: "leve", label: "Linha leve (carros)" },
  { id: "pesada", label: "Linha pesada (caminhão/ônibus)" },
  { id: "moto", label: "Motos" },
  { id: "agricola", label: "Agrícola" },
  { id: "industrial", label: "Industrial / máquinas" },
];

export const SERVICOS_BORRACHARIA = [
  { id: "conserto", label: "Conserto / remendo" },
  { id: "vulcanizacao", label: "Vulcanização" },
  { id: "montagem", label: "Montagem e desmontagem" },
  { id: "alinhamento", label: "Alinhamento" },
  { id: "balanceamento", label: "Balanceamento" },
  { id: "geometria", label: "Geometria / cambagem" },
  { id: "calibragem", label: "Calibragem / nitrogênio" },
  { id: "recapagem", label: "Recapagem / recauchutagem" },
  { id: "rodizio", label: "Rodízio de pneus" },
  { id: "conserto_roda", label: "Conserto de roda / aro" },
  { id: "movel", label: "Borracharia móvel (atende no local)" },
  { id: "socorro", label: "Socorro em rodovia" },
];

export const PRODUTOS_BORRACHARIA = [
  { id: "pneus_novos", label: "Pneus novos" },
  { id: "pneus_remold", label: "Pneus remold / recapados" },
  { id: "pneus_usados", label: "Pneus usados" },
  { id: "camaras", label: "Câmaras de ar" },
  { id: "protetores", label: "Protetores" },
  { id: "rodas", label: "Rodas e aros" },
  { id: "valvulas", label: "Válvulas" },
  { id: "contrapesos", label: "Contrapesos / chumbinhos" },
  { id: "parafusos", label: "Parafusos e porcas" },
  { id: "reparos", label: "Reparos e remendos" },
];

// ── Oficina de manutenção aeronáutica ───────────────────────────────
//
// DE ONDE VEM CADA LISTA — e por que importa.
//
// Duas fontes, e o formulário amarra as duas:
//
// 1. A ANAC, que diz o que uma organização de manutenção TEM de ter. Cada campo
//    que é requisito regulatório carrega `anac` com a seção da norma, e a tela
//    mostra "Exigência ANAC — <norma>" ao lado. Base conferida nos textos
//    oficiais em 01/10/2026: RBAC 145 EMD 09 (Res. 718/2023), RBAC 43 EMD 05,
//    RBAC 65 EMD 00, RBAC 91 EMD 07, IS 145-001H, IS 91.403-001D.
//
// 2. O sistema de Manutenção, que diz o que a OS AERONÁUTICA pergunta ao
//    prestador. Os ids abaixo são os MESMOS de lá, para o que o fornecedor marca
//    aqui casar com o que a equipe marca na abertura da OS:
//      tipos_asa         ↔ AircraftProfile::TIPOS_DE_ASA (fixa | rotativa)
//      capacidades       ↔ `capabilities` da certificação do prestador, que é o
//                          que Aviation::CertificacoesDoPrestador.elegivel_para?
//                          compara com a tarefa ("celula", "motor", "helice"…)
//      tipos_atendimento ↔ FleetTerms::APRESENTACAO_DOS_TIPOS (preventiva,
//                          corretiva, garantia, revisao, diagnostico)
//
// ⚠️ O QUE ISTO NÃO FAZ: declarar aqui não torna ninguém elegível lá. No sistema
// de Manutenção a certificação é anexada e CONFERIDA por quem contrata — o
// prestador não se autovalida. A ficha leva a declaração e os documentos; a
// elegibilidade nasce da conferência.
//
// As chaves são snake_case desde o nascimento. O posto aprendeu do jeito caro
// (`registroAnp` × `registro_anp`) que nome diferente nos dois lados vira campo
// nulo em silêncio.

export const SITUACAO_ANAC = [
  { id: "certificada_145", label: "Certificada pela ANAC — possui COM (RBAC 145)" },
  { id: "em_certificacao", label: "Em processo de certificação RBAC 145" },
  { id: "mma_43", label: "Não certificada — serviços por mecânico MMA habilitado (RBAC 43.3)" },
  { id: "apoio", label: "Fornecedor de peças / apoio — não executa manutenção" },
];

const EXIGE_COM = (p) => p.situacao_anac === "certificada_145" || p.situacao_anac === "em_certificacao";
const CERTIFICADA = (p) => p.situacao_anac === "certificada_145";
const EXECUTA = (p) => p.situacao_anac && p.situacao_anac !== "apoio";

// RBAC 145.59, redação da Resolução 718/2023. PMD = peso máximo de decolagem.
export const CATEGORIAS_CLASSES_145 = [
  { id: "celula_1", label: "Célula — Classe 1 (composto; até 5.670 kgf avião / 2.730 kgf helicóptero)" },
  { id: "celula_2", label: "Célula — Classe 2 (composto; acima desses limites)" },
  { id: "celula_3", label: "Célula — Classe 3 (metálica; até 5.670 / 2.730 kgf)" },
  { id: "celula_4", label: "Célula — Classe 4 (metálica; acima desses limites)" },
  { id: "motor_1", label: "Motor — Classe 1 (convencional até 400 hp)" },
  { id: "motor_2", label: "Motor — Classe 2 (convencional acima de 400 hp)" },
  { id: "motor_3", label: "Motor — Classe 3 (turbina)" },
  { id: "helice_1", label: "Hélice — Classe 1 (passo fixo ou ajustável no solo)" },
  { id: "helice_2", label: "Hélice — Classe 2 (demais hélices)" },
  { id: "radio_1", label: "Rádio — Classe 1 (comunicação)" },
  { id: "radio_2", label: "Rádio — Classe 2 (navegação)" },
  { id: "radio_3", label: "Rádio — Classe 3 (radar)" },
  { id: "instrumento_1", label: "Instrumento — Classe 1 (mecânico)" },
  { id: "instrumento_2", label: "Instrumento — Classe 2 (elétrico)" },
  { id: "instrumento_3", label: "Instrumento — Classe 3 (giroscópico)" },
  { id: "instrumento_4", label: "Instrumento — Classe 4 (eletrônico)" },
  { id: "acessorio_1", label: "Acessório — Classe 1 (mecânico)" },
  { id: "acessorio_2", label: "Acessório — Classe 2 (elétrico)" },
  { id: "acessorio_3", label: "Acessório — Classe 3 (eletrônico)" },
  { id: "servicos_especializados", label: "Serviços Especializados (classe única)" },
];

// RBAC 145.59(g)-I — exemplos de serviço especializado. Vale como lista do que
// a oficina faz mesmo sem ser a categoria dela.
export const SERVICOS_ESPECIALIZADOS_AERO = [
  { id: "end", label: "Ensaios não destrutivos (END)" },
  { id: "boroscopia", label: "Boroscopia" },
  { id: "soldagem", label: "Soldagem" },
  { id: "pintura", label: "Pintura de aeronave" },
  { id: "pesagem", label: "Pesagem e balanceamento (peso e CG)" },
  { id: "balanceamento_dinamico", label: "Balanceamento dinâmico de hélice / rotor" },
  { id: "vasos_pressao", label: "Vasos de pressão / extintores / oxigênio" },
  { id: "interiores", label: "Interiores / estofamento" },
  { id: "baterias", label: "Baterias aeronáuticas" },
  { id: "rodas_freios", label: "Rodas, pneus e freios" },
];

// ↔ AircraftProfile::TIPOS_DE_ASA no sistema de Manutenção
export const TIPOS_ASA = [
  { id: "fixa", label: "Asa fixa (aviões)" },
  { id: "rotativa", label: "Asa rotativa (helicópteros)" },
];

// ↔ `capabilities` da certificação do prestador no sistema de Manutenção.
// Os cinco primeiros são as categorias de componente de lá
// (AircraftComponent::CATEGORIAS); os demais são as categorias RBAC 145 que
// não têm componente próprio, escritas do mesmo jeito: minúsculas, sem acento.
export const CAPACIDADES_AERO = [
  { id: "celula", label: "Célula (estrutura)" },
  { id: "motor", label: "Motor (grupo motopropulsor)" },
  { id: "helice", label: "Hélice" },
  { id: "rotor", label: "Rotor (helicóptero)" },
  { id: "transmissao", label: "Transmissão (helicóptero)" },
  { id: "radio", label: "Rádio / aviônicos (com, nav, radar)" },
  { id: "instrumentos", label: "Instrumentos" },
  { id: "acessorios", label: "Acessórios (trem, hidráulico, elétrico…)" },
];

// ↔ FleetTerms::APRESENTACAO_DOS_TIPOS — os tipos da OS de manutenção
// aeronáutica, com o rótulo que a equipe vê ao abrir a OS.
export const TIPOS_ATENDIMENTO_AERO = [
  { id: "preventiva", label: "Manutenção programada / inspeção" },
  { id: "corretiva", label: "Manutenção corretiva / discrepância" },
  { id: "garantia", label: "Atendimento em garantia" },
  { id: "revisao", label: "Revisão de componente" },
  { id: "diagnostico", label: "Diagnóstico / avaliação técnica" },
];

// O que a manutenção programada da OS ("Manutenção programada a executar")
// costuma pedir. Cada item cita de onde vem a inspeção.
export const INSPECOES_AERO = [
  { id: "inspecao_anual", label: "Inspeção anual (RBAC 91.409(a))" },
  { id: "inspecao_100h", label: "Inspeção de 100 h (RBAC 91.409(b))" },
  { id: "inspecao_50h", label: "Inspeção de 50 h" },
  { id: "progressiva", label: "Inspeção progressiva (RBAC 91.409(d))" },
  { id: "programa_fabricante", label: "Programa do fabricante — checks A/B/C/D (RBAC 91.409(f)/(i))" },
  { id: "cva", label: "Vistoria e emissão de CVA (IS 91.403-001D)" },
  { id: "diretrizes", label: "Cumprimento de Diretrizes de Aeronavegabilidade (RBAC 39)" },
  { id: "boletins", label: "Boletins de serviço do fabricante" },
  { id: "vida_limitada", label: "Controle de componentes com vida limitada (RBAC 43.10)" },
  { id: "grandes_reparos", label: "Grandes reparos e alterações — F-400-04 / SEGVOO 001 (RBAC 43.9(d))" },
];

export const OPERACOES_ATENDIDAS = [
  { id: "rbac91", label: "Aviação geral / privada (RBAC 91)" },
  { id: "rbac90", label: "Aviação pública / segurança pública (RBAC 90)" },
  { id: "rbac135", label: "Táxi aéreo (RBAC 135) — segue o programa do operador (RBAC 145.205)" },
  { id: "rbac121", label: "Linha aérea (RBAC 121)" },
];

export const HABILITACOES_MMA = [
  { id: "cel", label: "CEL — Célula" },
  { id: "gmp", label: "GMP — Grupo motopropulsor" },
  { id: "avi", label: "AVI — Aviônicos" },
];

export const MANUAIS_145 = [
  { id: "mom", label: "MOM — Manual da Organização de Manutenção (RBAC 145.207/145.209)" },
  { id: "mcq", label: "MCQ — Manual de Controle da Qualidade (RBAC 145.211)" },
  { id: "lista_capacidade", label: "Lista de Capacidade atualizada (RBAC 145.215-I)" },
  { id: "sgso", label: "SGSO implantado — Manual de SGSO (RBAC 145.214-I)" },
  { id: "ptm", label: "Programa de Treinamento em Manutenção (RBAC 145.163)" },
  { id: "ppsp", label: "Prevenção ao uso de substâncias psicoativas — PPSP (RBAC 120)" },
  { id: "artigos_perigosos", label: "Treinamento em artigos perigosos (RBAC 145.165)" },
];

export const CONVALIDACOES = [
  { id: "faa", label: "FAA (EUA) — Part 145" },
  { id: "easa", label: "EASA (Europa) — Part-145" },
  { id: "tcca", label: "TCCA (Canadá)" },
  { id: "uk_caa", label: "UK CAA" },
  { id: "srvsop", label: "SRVSOP (América Latina)" },
];

const PERFIL_AERONAUTICO = {
  titulo: "Certificação ANAC e capacidades técnicas",
  veiculos: false,
  catalogo: false, // o catálogo terrestre da manutenção não tem tipo aeronáutico
  aviso: "Os campos marcados com “Exigência ANAC” são requisitos da Agência Nacional de Aviação Civil para organizações de manutenção aeronáutica. Informe exatamente como consta no seu COM e nas Especificações Operativas.",
  campos: [
    { tipo: "titulo", label: "1. Certificação da organização" },
    { key: "situacao_anac", tipo: "select", label: "Situação perante a ANAC", opcoes: SITUACAO_ANAC, obrigatorio: true, anac: "RBAC 145.5 e 43.3" },
    { key: "com_numero", tipo: "texto", label: "Nº do Certificado de Organização de Manutenção (COM)", placeholder: "Ex: 200311-02", obrigatorio: CERTIFICADA, quando: EXIGE_COM, anac: "RBAC 145.5 e 145.53", ajuda: "Preenchido sozinho quando o CNPJ consta nos dados abertos da ANAC." },
    { key: "anac_consulta", tipo: "leitura", label: "Situação nos dados abertos da ANAC", quando: (p) => !!p.anac_consulta },
    { key: "eo_numero", tipo: "texto", label: "Especificações Operativas (EO) — nº / revisão", placeholder: "Ex: EO nº 1234, rev. 05", quando: EXIGE_COM, anac: "RBAC 145.53(e)-I" },
    { key: "categorias_classes", tipo: "chips", label: "Categorias e classes do COM", opcoes: CATEGORIAS_CLASSES_145, min: 230, obrigatorio: CERTIFICADA, quando: EXIGE_COM, anac: "RBAC 145.59" },
    { key: "fabricantes_modelos", tipo: "area", label: "Fabricantes e modelos que constam na EO / Lista de Capacidade", placeholder: "Ex: Cessna 172/182/206, Embraer EMB-810, Helibras AS350 B2, Pratt & Whitney PT6A", quando: EXECUTA, anac: "RBAC 145.215-I" },
    { key: "convalidacoes", tipo: "chips", label: "Certificação estrangeira (convalidação)", opcoes: CONVALIDACOES, min: 150, quando: EXIGE_COM },
    { key: "registro_conselho", tipo: "texto", label: "Registro da empresa no CREA / CFT", placeholder: "Ex: CREA-SP 0000000", quando: EXIGE_COM, anac: "RBAC 145.51(f)-I" },

    { tipo: "titulo", label: "2. Responsáveis e pessoal técnico", quando: EXECUTA },
    { key: "gestor_responsavel", tipo: "texto", label: "Gestor Responsável (GR)", placeholder: "Nome completo", quando: EXIGE_COM, anac: "RBAC 145.151(a)-I" },
    { key: "responsavel_tecnico", tipo: "texto", label: "Responsável Técnico (RT)", placeholder: "Nome completo", obrigatorio: CERTIFICADA, quando: EXIGE_COM, anac: "RBAC 145.151(a)-II e Apêndice A" },
    { key: "rt_registro", tipo: "texto", label: "Registro do RT no CREA / CFT", placeholder: "Ex: CREA-SP 0000000000", quando: EXIGE_COM, anac: "RBAC 145.151 — Apêndice A" },
    { key: "gestor_sgso", tipo: "texto", label: "Gestor do SGSO", placeholder: "Pode ser o mesmo GR / RT", quando: EXIGE_COM, anac: "RBAC 145.151 — Apêndice B" },
    { key: "mma_nome", tipo: "texto", label: "Mecânico responsável (MMA)", placeholder: "Nome completo", obrigatorio: (p) => p.situacao_anac === "mma_43", quando: (p) => p.situacao_anac === "mma_43", anac: "RBAC 43.3(b) e RBAC 65" },
    { key: "mma_canac", tipo: "texto", label: "Código ANAC (CANAC) do mecânico", placeholder: "6 dígitos", obrigatorio: (p) => p.situacao_anac === "mma_43", quando: (p) => p.situacao_anac === "mma_43", anac: "RBAC 65.72" },
    { key: "qtd_mma", tipo: "numero", label: "Mecânicos MMA licenciados no quadro", placeholder: "Ex: 6", quando: EXECUTA, anac: "RBAC 145.157 e 145.213(d)" },
    { key: "habilitacoes_mma", tipo: "chips", label: "Habilitações dos mecânicos (RBAC 65)", opcoes: HABILITACOES_MMA, min: 150, quando: EXECUTA, anac: "RBAC 65.73" },
    { key: "qtd_inspetores", tipo: "numero", label: "Inspetores", placeholder: "Ex: 2", quando: EXIGE_COM, anac: "RBAC 145.155" },

    { tipo: "titulo", label: "3. Qualidade, segurança e registros", quando: EXIGE_COM },
    { key: "manuais", tipo: "chips", label: "Manuais e programas implantados", opcoes: MANUAIS_145, min: 250, quando: EXIGE_COM, anac: "RBAC 145.207 a 145.214-I" },
    { key: "calibracao", tipo: "simnao", label: "Ferramentas e equipamentos com calibração rastreável (INMETRO / RBC)?", quando: EXECUTA, anac: "RBAC 145.109(b)" },
    { key: "emite_cla", tipo: "simnao", label: "Emite Certificado de Liberação Autorizada — F-100-01 (SEGVOO 003)?", quando: EXIGE_COM, anac: "RBAC 43.9(d)-I e 145.219" },
    { key: "emite_cva", tipo: "simnao", label: "Emite CVA (Certificado de Verificação de Aeronavegabilidade)?", quando: EXIGE_COM, anac: "IS 91.403-001D — exige categoria Célula", ajuda: "Somente oficina com categoria Célula, para os modelos da sua EO." },
    { key: "registros_5_anos", tipo: "simnao", label: "Guarda os registros de manutenção por 5 anos após o retorno ao serviço?", quando: EXECUTA, anac: "RBAC 145.219" },

    { tipo: "titulo", label: "4. O que a oficina atende (cruza com a abertura da OS)" },
    { key: "tipos_asa", tipo: "chips", label: "Aeronaves atendidas", opcoes: TIPOS_ASA, min: 160, obrigatorio: true },
    { key: "capacidades", tipo: "chips", label: "Capacidades técnicas", opcoes: CAPACIDADES_AERO, min: 170, obrigatorio: EXECUTA, quando: EXECUTA, ajuda: "É o que o sistema confere ao indicar a oficina para uma tarefa da OS aeronáutica." },
    { key: "tipos_atendimento", tipo: "chips", label: "Tipos de OS que atende", opcoes: TIPOS_ATENDIMENTO_AERO, min: 200, quando: EXECUTA },
    { key: "inspecoes", tipo: "chips", label: "Inspeções e serviços de aeronavegabilidade", opcoes: INSPECOES_AERO, min: 250, quando: EXECUTA },
    { key: "servicos_especializados", tipo: "chips", label: "Serviços especializados", opcoes: SERVICOS_ESPECIALIZADOS_AERO, min: 200, quando: EXECUTA, anac: "RBAC 145.59(g)-I" },
    { key: "operacoes", tipo: "chips", label: "Operações que atende", opcoes: OPERACOES_ATENDIDAS, min: 250 },

    { tipo: "titulo", label: "5. Base e atendimento fora de sede" },
    { key: "aerodromo_base", tipo: "texto", label: "Aeródromo da base (indicativo ICAO)", placeholder: "Ex: SBMT, SDCO", ajuda: "Indicativo publicado pelo DECEA. Se houver mais de uma base, separe por vírgula." },
    { key: "hangar", tipo: "simnao", label: "Possui hangar próprio?" },
    { key: "atende_fora_sede", tipo: "simnao", label: "Atende fora da sede (equipe volante / aeronave AOG)?" },
    { key: "raio_km", tipo: "numero", label: "Raio de atendimento fora da sede (km)", placeholder: "Ex: 300", quando: (p) => p.atende_fora_sede === "sim" },
    { key: "plantao_aog", tipo: "texto", label: "Telefone de plantão AOG", placeholder: "(00) 00000-0000", quando: (p) => p.atende_fora_sede === "sim" },
  ],
  grupos: [],
};

// Definição de cada ramo:
//   veiculos          → mostra "Tipos de veículo atendidos"? (posto não usa)
//   servicosSugeridos → ids do catálogo da manutenção que fazem sentido no ramo.
//                       O resto continua acessível em "ver todos".
//   catalogo          → false esconde o catálogo de serviços da manutenção
//   aviso             → texto que abre o bloco (o aeronáutico explica a ANAC)
//   campos            → perguntas diretas. tipo: select | chips | numero |
//                       texto | area | simnao | leitura (vem de consulta
//                       oficial, não se edita) | titulo (subtítulo).
//                       Opcionais: obrigatorio (bool ou fn(perfil)),
//                       quando (fn(perfil) — some da tela e do resumo),
//                       anac (seção da norma: vira "Exigência ANAC — …"), ajuda,
//                       comercial (vai para "Proposta e condições" no PDF),
//                       interno (só a equipe vê/edita), invalido (fn(perfil)
//                       → mensagem quando o valor fere a regra).
//   derivar           → fn(perfil) → campos calculados gravados no envio
//   grupos            → checkbox que, ao marcar, abre a lista
export const PERFIS_ESTABELECIMENTO = {
  // Nomenclatura da Ficha Cadastral do Estabelecimento (Anexo II) do sistema de
  // Combustível — "4. Dados operacionais do posto" (campos.js:189-217). As
  // chaves seguem camelCase como as que já existiam; o outro lado guarda o
  // perfil inteiro em `perfil_json`.
  posto: {
    titulo: "Dados operacionais do posto",
    veiculos: false,
    servicosSugeridos: [3, 5, 10, 23, 25, 26, 30],
    campos: [
      { tipo: "titulo", label: "Registro e bandeira" },
      { key: "registroAnp", tipo: "texto", label: "Autorização ANP — revendedor varejista", placeholder: "Ex: 0000000-0", ajuda: "Preenchido sozinho pela consulta à ANP quando o CNPJ é encontrado." },
      { key: "bandeira", tipo: "select", label: "Bandeira", opcoes: BANDEIRAS_POSTO },
      { key: "distribuidora", tipo: "texto", label: "Distribuidora", placeholder: "Quem fornece o combustível ao posto" },
      { tipo: "titulo", label: "Pista" },
      { key: "combustiveis", tipo: "chips", label: "Produtos comercializados", opcoes: COMBUSTIVEIS, min: 150 },
      { key: "qtdBicos", tipo: "numero", label: "Nº de bicos", placeholder: "Ex: 8" },
      { key: "qtdBombas", tipo: "numero", label: "Nº de bombas", placeholder: "Ex: 4" },
      { key: "tanques", tipo: "texto", label: "Nº de tanques / capacidade (L)", placeholder: "Ex: 4 tanques / 60.000 L" },
      { key: "automacaoPista", tipo: "texto", label: "Automação de pista (qual)", placeholder: "Ex: Linx, Wayne Fusion, nenhuma" },
      { tipo: "titulo", label: "Funcionamento" },
      { key: "funciona24h", tipo: "simnao", label: "Funciona 24h?" },
      { key: "horarioFuncionamento", tipo: "texto", label: "Horário de funcionamento", placeholder: "Ex: 06:00 às 22:00", quando: (p) => p.funciona24h !== "sim" },
      { key: "atendeFds", tipo: "simnao", label: "Atende fins de semana / feriado?" },
      { key: "aceitaGalao", tipo: "simnao", label: "Aceita abastecimento em galão (com autorização)?" },
      { key: "internetPc", tipo: "simnao", label: "Internet + PC/impressora no local?" },
      // FICHA-COMB-3.0, item 8. `comercial` separa estes campos dos dados de
      // pista no PDF ("Proposta e condições"); `interno` só aparece para a
      // equipe — negociação não é pergunta que o posto responde sozinho.
      { tipo: "titulo", label: "Condições comerciais" },
      // O quadro da tabela desta ficha (padrão ou a que o comercial escolheu):
      // o posto lê os números antes de escolher e aceitar.
      { tipo: "quadro", linhas: (p) => quadroDaTabela(tabelaDaFicha(p)) },
      {
        key: "prazoRepasse", tipo: "select", comercial: true, obrigatorio: true,
        label: "Prazo de repasse e taxa", opcoes: (p) => prazosDaFicha(p),
        ajuda: "A taxa incide sobre o valor faturado (seu preço × litros) e é a única remuneração da InstaSolutions. Sem adesão, mensalidade, fidelidade ou multa. Dá para mudar depois, dentro da mesma tabela, valendo no período seguinte.",
        invalido: (p) => (p.prazoRepasse && taxaDoPrazo(p.prazoRepasse, p) === null ? "Prazo de repasse (não existe na tabela desta ficha)" : null),
      },
      {
        key: "taxaNegociada", tipo: "texto", comercial: true, interno: true,
        label: "Taxa negociada (%)", placeholder: "Ex: 1,8",
        ajuda: "Só se for MENOR que a da tabela para o prazo escolhido (Cl. 8.2.2). Vazio = taxa da tabela.",
        invalido: (p) => {
          if (!String(p.taxaNegociada ?? "").trim()) return null;
          const n = numeroBR(p.taxaNegociada);
          const teto = taxaDoPrazo(p.prazoRepasse, p);
          if (n === null || n < 0) return "Taxa negociada (número inválido)";
          if (teto !== null && n >= teto) return `Taxa negociada (precisa ser menor que ${String(teto).replace(".", ",")}%)`;
          return null;
        },
      },
      // Só pergunta quando a tabela oferece mais de uma forma; com uma só, ela
      // é derivada; sem nenhuma (tabela padrão), vale a conta indicada.
      {
        key: "formaPagamento", tipo: "select", comercial: true, obrigatorio: true,
        label: "Como receber o repasse", opcoes: (p) => formasDaFicha(p),
        quando: (p) => formasDaFicha(p).length > 1,
        invalido: (p) => (p.formaPagamento && formasDaFicha(p).length > 1 && !formasDaFicha(p).some((f) => f.id === p.formaPagamento) ? "Como receber o repasse (fora da tabela)" : null),
      },
      {
        key: "equipamentoProprio", tipo: "simnao", comercial: true, obrigatorio: true,
        label: "Vai usar maquininha da InstaSolutions?",
        ajuda: (p) => {
          const t = tabelaDaFicha(p);
          const aluguel = numeroBR(t.aluguelMensal);
          const valor = aluguel ? `${reaisBR(aluguel)} por mês, por equipamento${t.isencaoAcima ? ` — isento no mês em que o posto faturar mais de ${reaisBR(t.isencaoAcima)} pela plataforma` : ""}` : "Sem aluguel";
          return `Opcional. ${valor}. Sem caução, instalação, ativação ou multa por devolução.`;
        },
      },
      { key: "qtdEquipamentos", tipo: "numero", comercial: true, obrigatorio: true, label: "Quantas maquininhas", placeholder: "Ex: 2", quando: (p) => p.equipamentoProprio === "sim" },
      { key: "isencaoAluguelNegociada", tipo: "simnao", comercial: true, interno: true, label: "Isenção do aluguel negociada?", quando: (p) => p.equipamentoProprio === "sim" },
      {
        key: "capturaTerceiro", tipo: "simnao", comercial: true, obrigatorio: true,
        label: "Vai usar o app da InstaSolutions com maquininha/adquirente de terceiro?",
        ajuda: "Nessas transações a InstaSolutions não cobra taxa, mas também não há garantia nem prazo de repasse: o posto recebe direto do próprio adquirente (Cl. 8.4).",
      },
      { key: "adquirenteTerceiro", tipo: "texto", comercial: true, label: "Adquirente / operadora", placeholder: "Ex: Stone, Cielo, Rede", quando: (p) => p.capturaTerceiro === "sim" },
    ],
    // O que o sistema de combustível usa sem recalcular: a taxa da tabela
    // (FICHA-COMB-3.0, dicionário `taxa_percentual`), agora da tabela da ficha.
    // Forma de pagamento: com uma só na tabela, é ela; com várias, a escolhida.
    derivar: (p) => {
      const formas = formasDaFicha(p);
      return {
        taxaPercentual: taxaDoPrazo(p.prazoRepasse, p),
        formaPagamento: formas.length === 1 ? formas[0].id : formas.length > 1 ? p.formaPagamento : null,
      };
    },
    grupos: [
      { key: "servicos", label: "Serviços correlatos no posto", hint: "troca de óleo, calibragem, lava-jato…", opcoes: SERVICOS_POSTO, min: 165 },
      { key: "produtos", label: "Vendemos produtos", hint: "óleos, aditivos, palhetas…", opcoes: PRODUTOS_POSTO, min: 165 },
    ],
  },
  oficina_aeronautica: PERFIL_AERONAUTICO,
  guincho: {
    titulo: "Dados do guincho",
    veiculos: true,
    servicosSugeridos: [4, 10, 23],
    campos: [
      { key: "equipamentos", tipo: "chips", label: "Equipamentos disponíveis", opcoes: EQUIPAMENTOS_GUINCHO, min: 160 },
      { key: "capacidade", tipo: "select", label: "Capacidade máxima de reboque", opcoes: CAPACIDADES_GUINCHO },
      { key: "frota", tipo: "numero", label: "Quantos guinchos na frota", placeholder: "Ex: 3" },
      { key: "raioKm", tipo: "numero", label: "Raio de atendimento (km)", placeholder: "Ex: 80" },
    ],
    grupos: [
      { key: "remocoes", label: "Fazemos remoções especiais", hint: "sinistro, máquinas, motos…", opcoes: REMOCOES_GUINCHO, min: 165 },
    ],
  },
  borracharia: {
    titulo: "Dados da borracharia",
    veiculos: true,
    servicosSugeridos: [6, 7, 10],
    campos: [
      { key: "linhas", tipo: "chips", label: "Linhas de pneu atendidas", opcoes: LINHAS_BORRACHARIA, min: 160 },
    ],
    grupos: [
      { key: "servicos", label: "Prestamos serviços", hint: "conserto, vulcanização, balanceamento…", opcoes: SERVICOS_BORRACHARIA, min: 165 },
      { key: "produtos", label: "Vendemos produtos", hint: "pneus, câmaras, rodas…", opcoes: PRODUTOS_BORRACHARIA, min: 165 },
    ],
  },
};

// Posto em estado de condição definida no cadastro, sem condição ainda: as
// perguntas comerciais somem e entra o aviso. `condicoesADefinir` é marcado
// pela tela (formulário público e ficha da equipe) a partir do estado; o banco
// confere do lado dele (gatilho condicoes_comerciais_do_posto).
{
  const posto = PERFIS_ESTABELECIMENTO.posto;
  const i = posto.campos.findIndex((c) => c.tipo === "quadro");
  posto.campos = posto.campos.map((c) => (c.comercial || c.tipo === "quadro"
    ? { ...c, quando: (p) => !p.condicoesADefinir && (!c.quando || c.quando(p)) }
    : c));
  posto.campos.splice(i, 0, {
    tipo: "quadro", quando: (p) => !!p.condicoesADefinir,
    linhas: () => [
      "Condições comerciais a definir com o comercial",
      "Neste estado a taxa de cada posto é negociada no cadastro e vale só para você. Envie a ficha normalmente: o comercial da InstaSolutions define com você a taxa, o prazo de repasse e a maquininha, e você recebe o quadro para aceitar antes de começar a operar.",
    ],
  });
  const derivarOriginal = posto.derivar;
  posto.derivar = (p) => (p.condicoesADefinir
    ? { taxaPercentual: null, formaPagamento: null }
    : derivarOriginal(p));
}

// Perfil só existe para os ramos acima; os demais seguem com a ficha padrão.
export const perfilDe = (tipo) => PERFIS_ESTABELECIMENTO[tipo] || null;

// ── O que cada sistema usa da ficha ──────────────────────────────────────────
//
// Contrato em docs/INTEGRACAO_SISTEMAS.md. Os três sistemas recebem a MESMA
// ficha, mas cada um aproveita um pedaço:
//   manutenção   — catálogo de serviços (provider_service_types) e veículos;
//   combustível  — perfil do posto (bandeira, combustíveis) + registro na ANP;
//                  ignora service_type_ids de propósito ("não implementar aqui");
//   rastreamento — a rede de assistência: socorro, plantão, rodovias, 24h.
//
// Até 19/09/2026 o formulário era um só: o dono do posto respondia "tipos de
// veículo atendidos" e varria 30 serviços de oficina para achar os dele, e
// ninguém perguntava a bandeira a quem entrava só pela manutenção. Agora o
// link diz para quais sistemas a empresa entra, e o formulário pergunta só o
// que aqueles sistemas usam.
const SECOES_POR_SISTEMA = {
  manutencao:   { servicos: true,  veiculos: true,  socorro: true,  posto: false },
  combustivel:  { servicos: false, veiculos: false, socorro: false, posto: true  },
  abastecimento:{ servicos: false, veiculos: false, socorro: false, posto: true  },
  rastreamento: { servicos: true,  veiculos: true,  socorro: true,  posto: false },
};

// União do que os sistemas do link pedem. União, e não interseção: uma empresa
// que entra na manutenção E no combustível responde os dois blocos — esconder
// o que um deles usa seria perder dado.
export const secoesDaFicha = (sistemas, tipo) => {
  // Oficina de aeronave: só o bloco ANAC. O catálogo e os "tipos de veículo"
  // são da frota terrestre (não há tipo aeronáutico em provider_service_types),
  // e socorro em rodovia não é serviço de hangar — o atendimento fora de sede
  // (AOG) é pergunta do próprio perfil.
  if (tipo === "oficina_aeronautica") return { servicos: false, veiculos: false, socorro: false, posto: true };
  const arr = Array.isArray(sistemas) && sistemas.length ? sistemas : ["manutencao"];
  const out = { servicos: false, veiculos: false, socorro: false, posto: false };
  for (const s of arr) {
    const def = SECOES_POR_SISTEMA[s];
    if (!def) continue;
    for (const k of Object.keys(out)) out[k] = out[k] || def[k];
  }
  // O ramo também manda: posto que entra só pela manutenção continua tendo as
  // perguntas de posto, como antes. Sistema desconhecido no link não zera nada.
  if (perfilDe(tipo)) out.posto = true;
  if (!out.servicos && !out.posto) out.servicos = true;
  return out;
};

// A atividade principal que vem do CNPJ já diz o ramo. Sem isto o campo nasce
// em "Oficina Mecânica" e o dono do posto teria de descobrir sozinho que existe
// um seletor lá em cima para as perguntas certas aparecerem.
// Ordem importa: o específico vem antes do genérico.
const RAMO_POR_ATIVIDADE = [
  // CNAE 3316-3/01 e 3316-3/02 — "Manutenção e reparação de aeronaves". Vem
  // antes de tudo: o texto também casa com "manutenção e reparação" da oficina.
  { tipo: "oficina_aeronautica", re: /aeronave|aeron[áa]utic/i },
  { tipo: "posto", re: /combust[íi]ve|posto de gasolina|g[áa]s natural veicular|g[áa]s liquefeito/i },
  { tipo: "guincho", re: /reboque|guincho|socorro/i },
  { tipo: "borracharia", re: /pneum[áa]tic|pneu|borracharia|recauchut/i },
  { tipo: "funilaria", re: /funilaria|pintura de ve[íi]culo/i },
  { tipo: "eletrica", re: /el[ée]tric[oa]? de ve[íi]culo|instala[çc][ãa]o el[ée]tric/i },
  { tipo: "autopecas", re: /pe[çc]as e acess[óo]rios/i },
  { tipo: "oficina", re: /manuten[çc][ãa]o e repara[çc][ãa]o/i },
];

// O parceiro e a ficha usam listas diferentes: "autopeca" no singular aqui, e a
// lista do parceiro não tem guincho nem peças pesadas. Só converte o que existe
// dos dois lados — o resto fica sem sugestão, que é melhor do que sugerir errado.
const RAMO_FICHA_PARA_PARCEIRO = {
  oficina: "oficina", funilaria: "funilaria", borracharia: "borracharia",
  posto: "posto", autopecas: "autopeca", eletrica: "eletrica",
};

export function tipoParceiroPorAtividade(atividade) {
  const t = tipoPorAtividade(atividade);
  return t ? RAMO_FICHA_PARA_PARCEIRO[t] || null : null;
}

export function tipoPorAtividade(atividade) {
  const txt = String(atividade || "");
  if (!txt) return null;
  return RAMO_POR_ATIVIDADE.find((r) => r.re.test(txt))?.tipo || null;
}

// Marcar "Prestamos serviços" e não escolher nada não conta como preenchido.
const itensDoGrupo = (perfil, g) => (Array.isArray(perfil?.[g.key]) ? perfil[g.key] : []);

// O campo aparece para este perfil? (`quando` ausente = sempre)
export const campoVisivel = (c, perfil = {}) => !c.quando || !!c.quando(perfil || {});
// Opções e ajuda podem depender do próprio perfil (a tabela comercial da ficha).
export const opcoesDoCampo = (c, perfil = {}) => (typeof c.opcoes === "function" ? c.opcoes(perfil || {}) : c.opcoes || []);
export const ajudaDoCampo = (c, perfil = {}) => (typeof c.ajuda === "function" ? c.ajuda(perfil || {}) : c.ajuda || "");
const campoObrigatorio = (c, perfil = {}) =>
  typeof c.obrigatorio === "function" ? !!c.obrigatorio(perfil || {}) : !!c.obrigatorio;
const vazio = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length);

// Rótulos dos obrigatórios que faltam no perfil do ramo — o formulário público
// e o "Concluir" da equipe cobram a MESMA lista.
export function faltandoNoPerfil(tipo, perfil = {}) {
  const def = perfilDe(tipo);
  if (!def) return [];
  const visiveis = def.campos.filter((c) => c.key && campoVisivel(c, perfil));
  const faltando = visiveis
    .filter((c) => campoObrigatorio(c, perfil) && vazio(perfil?.[c.key]))
    .map((c) => c.label);
  // Valor preenchido mas fora da regra (ex.: taxa negociada acima da tabela)
  // trava o envio do mesmo jeito que um obrigatório vazio.
  const invalidos = visiveis.map((c) => (c.invalido ? c.invalido(perfil || {}) : null)).filter(Boolean);
  return [...faltando, ...invalidos];
}

export const perfilCampoObrigatorio = campoObrigatorio;

// O que vai para o banco: só o que estava à vista. Resposta de um campo que
// sumiu (a oficina mudou de "certificada" para "apoio") seria dado fantasma —
// um COM declarado por quem acabou de dizer que não tem.
export function limparPerfil(tipo, perfil = {}) {
  const def = perfilDe(tipo);
  if (!def) return {};
  const limpo = { ...(perfil || {}) };
  for (const c of def.campos) {
    if (c.key && !campoVisivel(c, perfil)) delete limpo[c.key];
  }
  if (def.derivar) {
    for (const [k, v] of Object.entries(def.derivar(limpo))) {
      if (v === null || v === undefined) delete limpo[k];
      else limpo[k] = v;
    }
  }
  return limpo;
}

// Resumo legível do perfil (card, PDF e CSV). Devolve [] quando não há ramo.
// `comercial`: true = só as condições comerciais; false = tudo menos elas
// (o PDF põe cada parte na sua seção); ausente = tudo.
export function resumoPerfil(tipo, perfil, { comercial } = {}) {
  const def = perfilDe(tipo);
  if (!def || !perfil) return [];
  const linhas = [];
  const nomes = (opcoes, ids) => (ids || []).map((id) => opcoes.find((o) => o.id === id)?.label).filter(Boolean);

  for (const c of def.campos) {
    if (!c.key || !campoVisivel(c, perfil)) continue;
    if (comercial !== undefined && !!c.comercial !== comercial) continue;
    const v = perfil[c.key];
    if (vazio(v)) continue;
    const opcoes = opcoesDoCampo(c, perfil);
    const texto = c.tipo === "chips" ? nomes(opcoes, v).join(", ")
      : c.tipo === "select" ? (opcoes.find((o) => o.id === v)?.label || String(v))
      : c.tipo === "simnao" ? (SIM_NAO.find((o) => o.id === v)?.label || String(v))
      : String(v);
    if (texto) linhas.push({ k: c.label, v: texto });
  }
  for (const g of comercial ? [] : def.grupos || []) {
    const marcados = nomes(g.opcoes, itensDoGrupo(perfil, g));
    if (marcados.length) linhas.push({ k: g.label, v: marcados.join(", ") });
  }
  return linhas;
}

// "Proposta e condições" do PDF: o que o posto escolheu no item 8 da
// FICHA-COMB-3.0. Vazio quando o ramo não tem condição comercial na ficha.
// A tabela vai na frente: "Mensal (30 dias) — taxa de 2%" só se entende
// sabendo de qual tabela saiu, e é o quadro inteiro que o posto aceitou.
export function condicoesComerciaisTexto(tipo, perfil) {
  const escolhas = resumoPerfil(tipo, perfil, { comercial: true }).map((l) => `${l.k}: ${l.v}`);
  if (!escolhas.length) return "";
  const def = perfilDe(tipo);
  const quadro = def?.campos.some((c) => c.tipo === "quadro") ? quadroDaTabela(tabelaDaFicha(perfil)) : [];
  return [...quadro, ...escolhas].join(". ");
}

// ── Modelo do formulário: Manutenção, Combustível ou Aeronáutico ────
//
// QUAIS SEÇÕES aparecem continua sendo decisão do `secoesDaFicha` (pelos
// sistemas do link e pelo ramo). O modelo é o que vem antes disso: o botão que
// a equipe aperta ao mandar o link, e o vocabulário do formulário.
//
//   tipos        → os ramos oferecidos no "Tipo de estabelecimento"
//   tipoPadrao   → onde a ficha nasce
//   sistemas     → sistemas marcados por padrão ao escolher o modelo
//
// Manutenção e Combustível são, na prática, atalhos para os sistemas — o
// formulário por sistema já existia. O Aeronáutico é o que muda o jogo: trava
// o ramo em `oficina_aeronautica`, que é o que liga as perguntas da ANAC. Ele
// entra no sistema de Manutenção, onde fica o módulo de aviação.
//
// O modelo viaja no token do link (`t`). Link antigo, sem `t`, cai no
// `modeloDaFicha`, que deduz pelo ramo e pelos sistemas — nada que já foi
// enviado muda de cara.
const TIPOS_TERRESTRES = TIPOS_ESTABELECIMENTO.filter((t) => t.id !== "oficina_aeronautica").map((t) => t.id);

export const MODELOS_FICHA = {
  manutencao: {
    id: "manutencao", label: "Manutenção veicular", curto: "Manutenção",
    titulo: "Credenciamento — Manutenção veicular",
    descricao: "Oficinas, autopeças, funilarias, borracharias, elétricas e guinchos da frota terrestre.",
    tipos: TIPOS_TERRESTRES, tipoPadrao: "oficina",
    sistemas: ["manutencao"],
    previa: ["Dados da empresa (preenche sozinho pelo CNPJ)", "Dados do responsável — nome * e foto do documento *", "Dados bancários", "Tipos de veículo e serviços de manutenção (catálogo do sistema de Manutenção)", "Socorro 24h / assistência em rodovias *"],
  },
  combustivel: {
    id: "combustivel", label: "Combustível (posto)", curto: "Combustível",
    titulo: "Credenciamento — Posto de combustível",
    descricao: "Postos revendedores: autorização ANP, bandeira, pista e funcionamento.",
    tipos: TIPOS_TERRESTRES, tipoPadrao: "posto",
    sistemas: ["combustivel"],
    previa: ["Dados da empresa (preenche sozinho pelo CNPJ)", "Autorização ANP, bandeira e combustíveis (preenche sozinho pela ANP)", "Dados do responsável — nome * e foto do documento *", "Dados bancários", "Pista: bicos, bombas, tanques, automação · Funcionamento 24h / fim de semana"],
  },
  aeronautico: {
    id: "aeronautico", label: "Aeronáutico (ANAC)", curto: "Aeronáutico",
    titulo: "Credenciamento — Manutenção aeronáutica",
    descricao: "Oficinas de aeronaves: COM, categorias e classes RBAC 145, responsáveis e capacidades.",
    tipos: ["oficina_aeronautica"], tipoPadrao: "oficina_aeronautica",
    sistemas: ["manutencao"],
    previa: ["Dados da empresa (preenche sozinho pelo CNPJ)", "COM, situação e categorias/classes (preenche sozinho pela ANAC) *", "Dados do responsável — nome * e foto do documento *", "Responsáveis RBAC 145 (GR, RT, SGSO), mecânicos MMA, manuais", "Aeronaves, capacidades e tipos de OS atendidos · base e AOG"],
  },
};

export const LISTA_MODELOS_FICHA = Object.values(MODELOS_FICHA);

// Qual modelo vale para uma ficha. `t` explícito vence; sem ele, o ramo diz
// (aeronáutica é aeronáutica; posto que só entra no Combustível é posto) e o
// resto é manutenção, que era a ficha única de antes.
export function modeloDaFicha({ t, tipo, sistemas } = {}) {
  if (t && MODELOS_FICHA[t]) return MODELOS_FICHA[t];
  if (tipo === "oficina_aeronautica") return MODELOS_FICHA.aeronautico;
  const sis = Array.isArray(sistemas) ? sistemas : [];
  if (tipo === "posto" && !sis.includes("manutencao")) return MODELOS_FICHA.combustivel;
  if (!tipo && sis.length && sis.every((s) => s === "combustivel" || s === "abastecimento")) return MODELOS_FICHA.combustivel;
  return MODELOS_FICHA.manutencao;
}

// Título e dica da seção do perfil/serviços, pelo que ela mostra.
export function tituloSecaoServicos(secoes, tipo) {
  if (tipo === "oficina_aeronautica") return { titulo: "Certificação ANAC e capacidades", hint: "COM, categorias RBAC 145 e o que a oficina atende" };
  if (secoes?.servicos) return { titulo: "Serviços prestados", hint: "Marque tudo o que a empresa atende" };
  return { titulo: "Dados do posto", hint: "O que o posto vende e como ele opera" };
}

export const DIAS_SEMANA = [
  { id: "seg", label: "Seg" },
  { id: "ter", label: "Ter" },
  { id: "qua", label: "Qua" },
  { id: "qui", label: "Qui" },
  { id: "sex", label: "Sex" },
  { id: "sab", label: "Sáb" },
  { id: "dom", label: "Dom" },
];
export const DIAS_UTEIS = ["seg", "ter", "qua", "qui", "sex"];

// Resumo legível do atendimento de socorro (usado no card, no PDF e no CSV)
export function resumoAtendimento(c = {}) {
  if (!c.fazSocorro) return "Não presta socorro/assistência";
  if (c.atende24h) return "24 horas, todos os dias";
  const dias = (c.diasAtendimento || []).map((d) => DIAS_SEMANA.find((x) => x.id === d)?.label).filter(Boolean);
  const faixa = c.horarioInicio && c.horarioFim ? `${c.horarioInicio} às ${c.horarioFim}` : "";
  return [dias.join(", "), faixa].filter(Boolean).join(" — ") || "Horário não informado";
}

export const CONTACT_CHANNELS = ["WhatsApp", "Telefone", "E-mail", "Indicação", "Visita Presencial", "LinkedIn", "Website"];

// ── Sistemas que o cliente potencial utiliza ──
export const CLIENT_SYSTEMS = [
  { id: "manutencao", label: "Manutenção", icon: "", color: "#7c3aed" },
  { id: "abastecimento", label: "Abastecimento", icon: "", color: "#0d9488" },
  { id: "rastreamento", label: "Rastreamento", icon: "", color: "#2563eb" },
];

export const LOSS_REASONS = ["Sem orçamento", "Escolheu concorrente", "Sem interesse no momento", "Frota muito pequena", "Não respondeu ao follow-up", "Condições não aceitas", "Outro"];
export const REGIONS = ["Grande São Paulo", "Interior SP", "Grande Rio", "Interior RJ", "Minas Gerais", "Sul", "Nordeste", "Centro-Oeste", "Norte", "Outra"];

// ── Estados Brasileiros (UF) ──
export const BRAZILIAN_STATES = [
  { uf: "AC", name: "Acre" },
  { uf: "AL", name: "Alagoas" },
  { uf: "AP", name: "Amapá" },
  { uf: "AM", name: "Amazonas" },
  { uf: "BA", name: "Bahia" },
  { uf: "CE", name: "Ceará" },
  { uf: "DF", name: "Distrito Federal" },
  { uf: "ES", name: "Espírito Santo" },
  { uf: "GO", name: "Goiás" },
  { uf: "MA", name: "Maranhão" },
  { uf: "MT", name: "Mato Grosso" },
  { uf: "MS", name: "Mato Grosso do Sul" },
  { uf: "MG", name: "Minas Gerais" },
  { uf: "PA", name: "Pará" },
  { uf: "PB", name: "Paraíba" },
  { uf: "PR", name: "Paraná" },
  { uf: "PE", name: "Pernambuco" },
  { uf: "PI", name: "Piauí" },
  { uf: "RJ", name: "Rio de Janeiro" },
  { uf: "RN", name: "Rio Grande do Norte" },
  { uf: "RS", name: "Rio Grande do Sul" },
  { uf: "RO", name: "Rondônia" },
  { uf: "RR", name: "Roraima" },
  { uf: "SC", name: "Santa Catarina" },
  { uf: "SP", name: "São Paulo" },
  { uf: "SE", name: "Sergipe" },
  { uf: "TO", name: "Tocantins" },
];

// ── Hierarquia de acessos ──
export const ROLES = {
  admin:     { label: "Administrador", level: 3 },
  gerente:   { label: "Gerente",       level: 2 },
  comercial: { label: "Comercial",     level: 1 },
};

// Quais módulos cada role pode acessar
export const ROLE_ACCESS = {
  admin:     ["dashboard", "clients", "client_pipeline", "rede", "partners", "partner_pipeline", "credenciamentos", "contratos_parceiros", "contracts", "satisfaction", "tasks", "reports", "management", "team"],
  gerente:   ["dashboard", "clients", "client_pipeline", "rede", "partners", "partner_pipeline", "credenciamentos", "contratos_parceiros", "contracts", "satisfaction", "tasks", "reports", "management", "team"],
  comercial: ["dashboard", "clients", "client_pipeline", "rede", "partner_pipeline", "credenciamentos", "contratos_parceiros", "contracts", "satisfaction", "tasks", "reports"],
};

export const SERVICES = [
  { id: "manutencao_prev", label: "Manutenção Preventiva" },
  { id: "manutencao_corr", label: "Manutenção Corretiva" },
  { id: "abastecimento", label: "Abastecimento (Rede Credenciada)" },
  { id: "controle_saldo", label: "Controle de Saldo/Limite" },
  { id: "relatorios", label: "Relatórios Gerenciais" },
  { id: "rastreamento", label: "Rastreamento/Monitoramento" },
  { id: "sinistro", label: "Gestão de Sinistros" },
];

// ── Contratos ──
export const CONTRACT_STATUSES = [
  { id: "rascunho", label: "Rascunho", color: "#94a3b8" },
  { id: "ativo", label: "Ativo", color: "#059669" },
  { id: "vencendo", label: "Vencendo", color: "#f59e0b" },
  { id: "vencido", label: "Vencido", color: "#ef4444" },
  { id: "cancelado", label: "Cancelado", color: "#cbd5e1" },
];

// ── Satisfação / NPS ──
export const NPS_CATEGORIES = [
  { id: "onboarding", label: "Onboarding", color: "#6366f1" },
  { id: "suporte", label: "Suporte", color: "#3b82f6" },
  { id: "geral", label: "Geral", color: "#0d9488" },
  { id: "renovacao", label: "Renovação", color: "#f59e0b" },
  { id: "servico", label: "Serviço", color: "#8b5cf6" },
];

export const NAV_ITEMS = [
  { id: "dashboard", label: "Painel do Dia", icon: "BarChart3", section: null },
  { id: "clients", label: "Clientes (Frota)", icon: "Truck", section: "FRENTE 1 — CLIENTES" },
  { id: "client_pipeline", label: "Pipeline Clientes", icon: "Target", section: null },
  // Uma aba só: cadastro, contato e credenciamento da mesma empresa. As duas
  // antigas continuam existindo por enquanto (a de fichas tem telas que ainda
  // não migraram), mas o caminho do dia a dia é este.
  { id: "rede", label: "Rede de Parceiros", icon: "Wrench", section: "FRENTE 2 — PARCEIROS" },
  { id: "partner_pipeline", label: "Pipeline Parceiros", icon: "Target", section: null },
  { id: "credenciamentos", label: "Fichas (documentação)", icon: "ClipboardCheck", section: null },
  { id: "contratos_parceiros", label: "Contratos de Parceiros", icon: "FileSignature", section: null },
  { id: "contracts", label: "Contratos", icon: "FileText", section: "PÓS-VENDA" },
  { id: "satisfaction", label: "Satisfação & NPS", icon: "SmilePlus", section: null },
  { id: "tasks", label: "Tarefas & Follow-ups", icon: "CheckSquare", section: "PRODUTIVIDADE" },
  { id: "reports", label: "Relatório Diário", icon: "PieChart", section: null },
  { id: "management", label: "Gerência", icon: "BarChart3", section: "GERÊNCIA" },
  { id: "team", label: "Equipe & Acessos", icon: "Users", section: "ADMINISTRAÇÃO" },
];

export const MOD_TITLES = {
  dashboard: "Painel do Dia",
  clients: "Clientes — Empresas com Frota",
  client_pipeline: "Pipeline de Clientes",
  rede: "Rede de Parceiros — prospecção, contato e aceite",
  partners: "Parceiros — Rede Credenciada",
  partner_pipeline: "Pipeline de Parceiros",
  credenciamentos: "Fichas — documentação de quem já aceitou",
  contratos_parceiros: "Contratos de Parceiros — termos e vias para download",
  contracts: "Contratos",
  satisfaction: "Satisfação & NPS",
  tasks: "Tarefas & Follow-ups",
  reports: "Relatório para Gerente de Operações",
  management: "Gerência — Relatórios Semanais",
};
